<script lang="ts">
  import { onMount, onDestroy, tick } from "svelte";
  import { push } from "svelte-spa-router";
  import { api } from "../../framework/js/api.js";
  import { auth } from "../../framework/js/auth.js";
  import { spinner } from "../../framework/js/spinner.js";
  import { toast } from "../../framework/js/toast.js";
  import ConfirmDialog from "../ConfirmDialog.svelte";
  import { relativeTime } from "../../framework/components/app/assessmentFormat";
  import BriefBuilderPaused from "../brief-builder/BriefBuilderPaused.svelte";
  import BriefBuilderAnswersPanel from "../brief-builder/BriefBuilderAnswersPanel.svelte";
  import { fromApiAnswers, titleFromDecision } from "../brief-builder/answers";
  import { buildSteps } from "../brief-builder/questions";
  import { emptyAnswers, type BriefBuilderAnswers } from "../brief-builder/types";
  import { formatBriefLine, parseTimingLines } from "./formatBriefLine";
  import { sanitizeHtml } from "../../framework/js/sanitizeHtml";
  import { parseQuestionMetadata } from "../../stores/BriefStepStore.js";

  const GEN_JOB_STORAGE_KEY = "briefGenerationJobUuid_";
  const GEN_PROGRESS_STORAGE_KEY = "briefGenerationInProgress_";
  const GENERATION_POLL_MS = 2500;

  let isGenerating = false;
  let generationPollTimer: ReturnType<typeof setTimeout> | null = null;
  let generationJobUuid: string | null = null;

  export let briefId: string;
  /** When true, shows simplified admin view: no history, "Their answers" tab, back to requests */
  export let adminMode = false;
  /**
   * When true, this brief was created via the consultative Brief Builder flow.
   * Navigation stays under /brief-builder instead of legacy /brief(s).
   */
  export let builderFlow = false;

  $: listPath = builderFlow ? '/brief-builder' : '/briefs';
  $: editPath = builderFlow ? `/brief-builder/${briefId}/continue` : `/brief/${briefId}/edit`;
  $: continuePath = builderFlow ? `/brief-builder/${briefId}/continue` : `/brief/${briefId}/continue`;

  let briefData: {
    sections: any[];
    methodology_alignment?: string;
    methodology_decline_reason?: string;
    scores?: Record<string, any>;
  } = { sections: [] };
  let briefName: string = "";
  let briefGenerated = false;
  let hasDraft = false; // Will be populated from backend later
  let showDiscardDraftDialog = false;
  let showDiscardBriefDialog = false;
  let hasCompletedVersions = false; // Track if brief has any completed versions

  // Version history - loaded from backend
  let versionHistory: any[] = [];
  
  // Brief answers - loaded from backend
  let briefAnswers: any[] = [];
  let briefQuestions: any[] = [];
  let originalBriefAnswers: any[] = []; // Store original current answers
  let originalBriefData: { sections: any[] } = { sections: [] }; // Store original content
  let isViewingOldVersion: boolean = false; // Track if viewing a version without answers
  let currentViewingVersion: number | null = null; // Track which version we're viewing
  let editingVersionTitle: number | null = null; // Track which version title is being edited
  let editTitleValue: string = '';
  let editingBriefTitle: boolean = false; // Track if brief title is being edited
  let briefTitleInput: HTMLInputElement | undefined;
  let editBriefTitleValue: string = '';
  let xploritUuid: string | null = null; // Store xplorit UUID for updating name
  let briefType: any = null; // Store brief type info (for scoring)
  /** Builder-flow consultation answers (when builderFlow). */
  let builderAnswers: BriefBuilderAnswers = emptyAnswers();

  // Active tab
  let activeTab: 'content' | 'answers' = 'content';

  $: showBuilderPaused = builderFlow && !hasCompletedVersions && !adminMode;

  function isClientEchoSection(name: unknown): boolean {
    return /what you told me/i.test(String(name || ''));
  }

  function fieldLabelsForQuestion(question: any): string[] {
    const meta = parseQuestionMetadata(question?.metadata || question?.question_metadata);
    const fields = [
      ...(Array.isArray(meta.fields) ? meta.fields : []),
      ...(Array.isArray(meta.addons) ? meta.addons : []),
    ] as Array<{ label?: string }>;
    return fields.map((field) => String(field.label || '').trim()).filter(Boolean);
  }

  function parseLabeledAnswerFields(text: string, labels: string[] = []): Array<{ label: string; value: string }> {
    const raw = (text || '').trim();
    if (!raw) return [];

    const contentLines = raw.split(/\r?\n/).map((line) => line.trim()).filter(Boolean);
    const linePairs: Array<{ label: string; value: string }> = [];
    for (const line of contentLines) {
      const match = line.match(/^(.+?):\s*(.*)$/);
      if (match) linePairs.push({ label: match[1].trim(), value: match[2].trim() });
    }
    if (contentLines.length >= 2 && linePairs.length === contentLines.length) return linePairs;
    if (contentLines.length !== 1) return [];

    const known = [...labels].filter(Boolean).sort((a, b) => b.length - a.length);
    if (known.length === 0) return linePairs;

    const pattern = new RegExp(`(${known.map((label) => label.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).join('|')})\\s*:\\s*`, 'g');
    const hits: Array<{ label: string; index: number; end: number }> = [];
    let found: RegExpExecArray | null;
    while ((found = pattern.exec(raw)) !== null) {
      hits.push({ label: found[1], index: found.index, end: found.index + found[0].length });
    }
    if (hits.length < 2) return linePairs;

    return hits.map((hit, i) => ({
      label: hit.label,
      value: raw.slice(hit.end, i + 1 < hits.length ? hits[i + 1].index : raw.length).trim(),
    }));
  }

  function areaList(section: any): Array<{ title?: string; question?: string; why_it_matters?: string }> {
    return Array.isArray(section?.areas) ? section.areas : [];
  }

  function leftOutList(section: any): Array<{ item?: string; reason?: string }> {
    return Array.isArray(section?.not_covering) ? section.not_covering : [];
  }

  function methodSteps(section: any): Array<{ name?: string; timing?: string; what_we_do?: string; what_you_get?: string }> {
    return Array.isArray(section?.steps) ? section.steps : [];
  }

  function isFindOut(section: any): boolean {
    return areaList(section).length > 0;
  }

  function isMethod(section: any): boolean {
    return methodSteps(section).length > 0 || Boolean(section?.recommendation);
  }

  function namedCards(section: any): Array<{ title: string; body: string }> {
    const name = String(section?.name || "");
    if (!/competitor|framework/i.test(name) || !Array.isArray(section?.text)) return [];
    const cards: Array<{ title: string; body: string }> = [];
    for (const line of section.text) {
      const match = String(line || "").trim().match(/^(.{3,60}):\s+(.+)$/);
      if (!match) return [];
      cards.push({ title: match[1], body: match[2] });
    }
    return cards;
  }

  function displayLabel(name: unknown): string {
    return String(name || "Section").replace(/^\s*\d+\.\s*/, "");
  }

  function isProblem(section: any): boolean {
    return /problem/i.test(String(section?.name || ""));
  }

  function isNumberedList(section: any): boolean {
    return /hypothes|objective|unknown/i.test(String(section?.name || ""));
  }

  function sectionLines(section: any): string[] {
    return Array.isArray(section?.text) ? section.text.map((line: unknown) => String(line ?? "")) : [];
  }

  function stripLeadNumber(line: string): string {
    return line.replace(/^\s*\d+[.)]\s+/, "").trim();
  }

  $: researchSections = (briefData?.sections || []).filter(
    (section: { name?: string }) => !isClientEchoSection(section?.name)
  );

  /** One row per question — draft completion previously duplicated every answer. */
  $: uniqueBriefAnswers = (() => {
    const seen = new Set<string>();
    const out: any[] = [];
    for (const a of briefAnswers) {
      const key = a.xplorit_type_brief_question_uuid || a.uuid;
      if (!key || seen.has(key)) continue;
      seen.add(key);
      out.push(a);
    }
    return out;
  })();

  /**
   * Scores for builder steps — text answers only.
   * Territory / practicalities (option picks) are never scored.
   */
  $: builderStepScores = (() => {
    const steps = buildSteps(builderAnswers);
    const scores = briefData?.scores;
    const byStep = scores?.by_step && typeof scores.by_step === 'object' ? scores.by_step : null;

    if (byStep) {
      return steps.map((s) => {
        if (s.kind !== 'text') return { score: null, feedback: null };
        const row = byStep[s.id] || null;
        return {
          score: row?.score ?? null,
          feedback: row?.feedback ?? null,
        };
      });
    }

    // Legacy payloads: q1…q7 were mixed with option steps — only attach to text steps.
    let qi = 0;
    return steps.map((s) => {
      if (s.kind !== 'text') return { score: null, feedback: null };
      qi += 1;
      return {
        score: scores?.[`q${qi}`] ?? null,
        feedback: scores?.[`q${qi}_feedback`] ?? null,
      };
    });
  })();

  // Proposal request modal state
  let showProposalModal: boolean = false;
  let proposalForm = {
    contact_name: '',
    email: '',
    phone: '',
    notes: ''
  };
  let proposalSubmitting: boolean = false;
  let proposalSubmitted: boolean = false;

  // Draft data - loaded from backend
  let draftData: {
    hasActiveDraft: boolean;
    versionNumber: number | null;
    createdAt: Date | null;
  } = {
    hasActiveDraft: false,
    versionNumber: null,
    createdAt: null
  };

  function formatDateTime(date: Date): string {
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  }

  function formatRelativeTime(date: Date): string {
    const s = relativeTime(date);
    return s || formatDateTime(date);
  }

  function getAlignmentClass(status: string): string {
    const statusLower = status.toLowerCase();
    if (statusLower === 'excellent') return 'alignment-excellent';
    if (statusLower === 'good') return 'alignment-good';
    if (statusLower === 'average') return 'alignment-average';
    if (statusLower === 'poor') return 'alignment-poor';
    return 'alignment-default';
  }

  function getAlignmentIcon(status: string): string {
    const statusLower = status.toLowerCase();
    if (statusLower === 'excellent') return 'check circle';
    if (statusLower === 'good') return 'check';
    if (statusLower === 'average') return 'minus circle';
    if (statusLower === 'poor') return 'warning circle';
    return 'circle';
  }

  function handleModifyVersion(versionNumber: number) {
    // Clear ALL localStorage for this brief to ensure fresh start
    localStorage.removeItem(`localBriefCurrentStep_${briefId}`);
    localStorage.removeItem(`localBriefAnswers_${briefId}`);
    localStorage.removeItem(`brief_guest`);
    
    // Clear any old draft base version
    Object.keys(localStorage).forEach(key => {
      if (key.startsWith(`draftBaseVersion_`)) {
        localStorage.removeItem(key);
      }
    });
    
    // Store which version we're basing the draft on
    localStorage.setItem(`draftBaseVersion_${briefId}`, versionNumber.toString());
    
    // Force a small delay to ensure localStorage is cleared
    setTimeout(() => {
      // Builder flow reopens the consultation; legacy opens edit/rework.
      push(editPath);
    }, 100);
  }

  function handleResumeBrief() {
    if (isGenerating) return;
    push(continuePath);
  }

  function handleDiscardBrief() {
    showDiscardBriefDialog = true;
  }
  
  async function confirmDiscardBrief() {
    try {
      const response: any = await api.deleteBrief(briefId);
      if (response.success) {
        showDiscardBriefDialog = false;
        push(listPath);
      } else {
        console.error('[BriefView] Error deleting brief:', response.message || 'Unknown error');
        showDiscardBriefDialog = false;
        push(listPath);
      }
    } catch (error) {
      console.error('[BriefView] Error deleting brief:', error);
      showDiscardBriefDialog = false;
      push(listPath);
    }
  }
  
  function cancelDiscardBrief() {
    showDiscardBriefDialog = false;
  }

  function handleResumeDraft() {
    if (isGenerating) return;
    push(editPath);
  }

  function handleDiscardDraft() {
    showDiscardDraftDialog = true;
  }
  
  async function confirmDiscardDraft() {
    try {
      // If there are no completed versions, discard the entire brief (not just the draft)
      if (!hasCompletedVersions) {
        // Delete the entire brief since there's nothing else to keep
        const response: any = await api.deleteBrief(briefId);
        if (response.success) {
          showDiscardDraftDialog = false;
          push(listPath);
        } else {
          console.error('[BriefView] Error deleting brief:', response.message || 'Unknown error');
          showDiscardDraftDialog = false;
          push(listPath);
        }
        return;
      }
      
      // If there are completed versions, just delete the draft
      const response: any = await api.deleteDraftVersion(briefId);
      if (response.success) {
        draftData.hasActiveDraft = false;
        draftData.versionNumber = null;
        draftData.createdAt = null;
        
        // Clear localStorage answers and step to reset to last completed version
        localStorage.removeItem(`localBriefAnswers_${briefId}`);
        localStorage.removeItem(`localBriefCurrentStep_${briefId}`);
        
        // Reload version history to remove draft
        const versionsResponse: any = await api.getBriefVersions(briefId);
        if (versionsResponse?.success && versionsResponse?.data?.versions) {
        versionHistory = versionsResponse.data.versions.map((v: any) => ({
          uuid: v.uuid,
          version: v.version,
          title: v.title,
          date: new Date(v.created_at),
          alignmentPercent: v.alignment_percent ?? null,
          alignmentStatus: v.alignment_status || 'unknown',
          isCurrent: v.is_current,
          status: v.status
        }));
        }
        
      }
    } catch (error) {
      console.error('[BriefView] Error deleting draft:', error);
    } finally {
      showDiscardDraftDialog = false;
    }
  }
  
  function cancelDiscardDraft() {
    showDiscardDraftDialog = false;
  }

  function startEditingTitle(version: any, event: Event) {
    event.stopPropagation();
    editingVersionTitle = version.version;
    editTitleValue = version.title || '';
  }

  async function saveVersionTitle(version: any, event: Event) {
    event.stopPropagation();
    
    if (!editTitleValue.trim()) {
      editingVersionTitle = null;
      return;
    }
    
    try {
      const response: any = await api.updateBriefVersionTitle(briefId, version.version, editTitleValue.trim());
      if (response?.success) {
        // Update the version in local state
        versionHistory = versionHistory.map(v => 
          v.version === version.version ? {...v, title: editTitleValue.trim()} : v
        );
      }
    } catch (error) {
      console.error('[BriefView] Error updating version title:', error);
    } finally {
      editingVersionTitle = null;
    }
  }

  function cancelEditingTitle(event: Event) {
    event.stopPropagation();
    editingVersionTitle = null;
    editTitleValue = '';
  }

  async function startEditingBriefTitle() {
    editingBriefTitle = true;
    editBriefTitleValue = briefName;
    await tick();
    briefTitleInput?.focus();
  }

  async function saveBriefTitle() {
    if (!editBriefTitleValue.trim()) {
      editingBriefTitle = false;
      return;
    }
    
    try {
      const response: any = await api.updateBriefName(briefId, editBriefTitleValue.trim());
      if (response?.success) {
        briefName = editBriefTitleValue.trim();
      }
    } catch (error) {
      console.error('[BriefView] Error updating brief title:', error);
    } finally {
      editingBriefTitle = false;
    }
  }

  function cancelEditingBriefTitle() {
    editingBriefTitle = false;
    editBriefTitleValue = '';
  }

  async function handleDownloadPdf() {
    const versionToDownload = currentViewingVersion;
    try {
      await api.downloadBriefPdf(briefId, versionToDownload);
    } catch (error) {
      console.error('[BriefView] Error downloading PDF:', error);
    }
  }

  let startingGapfinder = false;
  async function handleReviewWithGapfinder() {
    if (startingGapfinder) return;
    startingGapfinder = true;
    spinner.show('Preparing GapFinder review…');
    try {
      const create: any = await api.createGapfinderFromBrief({ xplorit_brief_uuid: briefId });
      const uuid = create?.data?.analysis?.uuid || create?.data?.uuid;
      if (!create?.success || !uuid) {
        toast.showNotification(false, create?.message || 'Could not start GapFinder review');
        return;
      }
      // Fire-and-forget generation; the view page will poll status.
      try {
        await api.enqueueGapfinderGenerationAsync(uuid);
      } catch (e) {
        console.error('[BriefView] Failed to enqueue GapFinder generation', e);
      }
      push(`/gapfinder/${uuid}`);
    } finally {
      startingGapfinder = false;
      spinner.hide();
    }
  }

  function openProposalModal() {
    // Pre-fill from logged-in user data
    const user = $auth?.user;
    proposalForm = {
      contact_name: user ? `${user.first_name || ''} ${user.last_name || ''}`.trim() : '',
      email: user?.email || '',
      phone: '',
      notes: ''
    };
    proposalSubmitted = false;
    showProposalModal = true;
  }

  function closeProposalModal() {
    showProposalModal = false;
    proposalForm = {
      contact_name: '',
      email: '',
      phone: '',
      notes: ''
    };
  }

  async function handleSubmitProposal() {
    // Name and email are auto-filled from logged-in user
    if (!proposalForm.contact_name || !proposalForm.email) {
      // This shouldn't happen if user is logged in, but just in case
      const user = $auth?.user;
      if (user) {
        proposalForm.contact_name = `${user.first_name || ''} ${user.last_name || ''}`.trim();
        proposalForm.email = user.email || '';
      }
      if (!proposalForm.contact_name || !proposalForm.email) {
        return;
      }
    }

    proposalSubmitting = true;
    try {
      const response: any = await api.submitProposalRequest(briefId, proposalForm);
      if (response.success) {
        proposalSubmitted = true;
        // Auto-close after showing success message
        setTimeout(() => {
          closeProposalModal();
        }, 3000);
      } else {
        console.error('[BriefView] Error submitting proposal request:', response.message);
      }
    } catch (error) {
      console.error('[BriefView] Error submitting proposal request:', error);
    } finally {
      proposalSubmitting = false;
    }
  }

  async function handleViewVersion(version: number) {
    
    // If clicking the same version we're viewing, don't reload
    if (currentViewingVersion === version) {
      return;
    }
    
    spinner.show();
    try {
      // Fetch specific version data with answers
      const versionResponse: any = await api.getBriefVersion(briefId, version);
      
      if (versionResponse?.success && versionResponse?.data) {
        // Load version content
        if (versionResponse.data.version?.response_summary) {
          briefData = JSON.parse(versionResponse.data.version.response_summary);
          briefGenerated = true;
        }
        
        // Load version-specific answers
        const answersData = versionResponse.data.answers;
        
        // Check if this version is the latest/current one
        const isLatestVersion = versionHistory.find(v => v.version === version)?.isCurrent;
        
        if (answersData && typeof answersData === 'object') {
          // Convert object to array if needed (Laravel may return object with numeric keys)
          const answersArray = Array.isArray(answersData) ? answersData : Object.values(answersData);
          
          if (answersArray.length > 0) {
            // Map the answer data to our expected format
            briefAnswers = answersArray.map((a: any) => ({
              uuid: a.uuid,
              xplorit_type_brief_question_uuid: a.question_uuid || a.xplorit_type_brief_question_uuid,
              brief_type_answer: a.answer || a.brief_type_answer,
              score: a.score || null,
              feedback: a.feedback || null,
              created_at: a.created_at
            }));
            isViewingOldVersion = false;
          } else {
            // Old version without answer snapshot
            if (isLatestVersion) {
              // For the latest version, show current answers
              briefAnswers = [...originalBriefAnswers];
              isViewingOldVersion = false;
            } else {
              // For old versions, don't show any answers
              briefAnswers = [];
              isViewingOldVersion = true;
            }
          }
        } else {
          // No answers data at all
          if (isLatestVersion) {
            briefAnswers = [...originalBriefAnswers];
            isViewingOldVersion = false;
          } else {
            briefAnswers = [];
            isViewingOldVersion = true;
          }
        }
        
        // Track which version we're viewing
        currentViewingVersion = version;
        
      }
    } catch (error) {
      console.error('[BriefView] Error loading version:', error);
    } finally {
      spinner.hide();
    }
  }
  
  function handleViewLatest() {
    // Restore original data
    briefData = {...originalBriefData};
    briefAnswers = [...originalBriefAnswers];
    isViewingOldVersion = false;
    
    // Find the latest version
    if (versionHistory.length > 0) {
      const latest = versionHistory.find(v => v.isCurrent);
      currentViewingVersion = latest ? latest.version : null;
    }
    
  }

  function handleBackToBriefs() {
    push(listPath);
  }

  function readStoredJobUuid(): string | null {
    try {
      return (
        (typeof sessionStorage !== "undefined" ? sessionStorage.getItem(GEN_JOB_STORAGE_KEY + briefId) : null) ||
        (typeof localStorage !== "undefined" ? localStorage.getItem(GEN_JOB_STORAGE_KEY + briefId) : null) ||
        null
      );
    } catch { return null; }
  }

  function clearGenerationState() {
    if (generationPollTimer) { clearTimeout(generationPollTimer); generationPollTimer = null; }
    isGenerating = false;
    generationJobUuid = null;
    try {
      sessionStorage.removeItem(GEN_JOB_STORAGE_KEY + briefId);
      localStorage.removeItem(GEN_JOB_STORAGE_KEY + briefId);
      sessionStorage.removeItem(GEN_PROGRESS_STORAGE_KEY + briefId);
      localStorage.removeItem(GEN_PROGRESS_STORAGE_KEY + briefId);
    } catch (_) {}
  }

  async function pollGenerationJob() {
    if (!generationJobUuid) return;
    try {
      const st: any = await api.getAiJobStatus(generationJobUuid);
      if (st?.success && st?.data) {
        const status = st.data.status;
        if (status === "completed") {
          clearGenerationState();
          try { await api.updateUserCredit(); } catch (_) {}
          await loadBriefData();
          toast.showNotification(true, "Brief generated");
          return;
        }
        if (status === "failed") {
          const msg = st.data.error_message || "Generation failed";
          clearGenerationState();
          toast.showNotification(false, msg);
          return;
        }
      }
    } catch (e) {
      console.error("[BriefView] poll error", e);
    }
    generationPollTimer = setTimeout(pollGenerationJob, GENERATION_POLL_MS);
  }

  function startGenerationPolling(jobUuid: string) {
    generationJobUuid = jobUuid;
    isGenerating = true;
    if (generationPollTimer) clearTimeout(generationPollTimer);
    generationPollTimer = setTimeout(pollGenerationJob, GENERATION_POLL_MS);
  }

  async function loadBriefData() {
    // Set a default title immediately
    briefName = 'Untitled Brief';

    spinner.show();
    try {
      const [versionsResponse, response, draftResponse, builderResponse]: [any, any, any, any] =
        await Promise.all([
          api.getBriefVersions(briefId),
          api.getExistingBriefResponse(briefId),
          api.getDraftVersion(briefId),
          builderFlow ? api.getBriefBuilder(briefId) : Promise.resolve(null),
        ]);

      const summaryData = response?.data;

      if (summaryData) {
        let extractedName =
          summaryData.xplorit?.name ||
          summaryData.name ||
          summaryData.project_name ||
          'Untitled Brief';
        if (extractedName && (extractedName.length > 100 || extractedName.includes('\n'))) {
          console.warn('[BriefView] Brief name appears to be an answer (length: ' + extractedName.length + '), resetting to Untitled Brief');
          extractedName = 'Untitled Brief';
        }
        briefName = extractedName;
        xploritUuid = summaryData.xplorit?.uuid ?? summaryData.xplorit_uuid ?? null;
        briefType = summaryData.xplorit_type_brief ?? null;
      }

      if (builderFlow && builderResponse?.success && builderResponse?.data) {
        const bb = builderResponse.data.brief || builderResponse.data;
        builderAnswers = fromApiAnswers(bb.builder_answers);
        if (bb.name && typeof bb.name === 'string' && bb.name.trim()) {
          const n = bb.name.trim();
          if (n.length <= 100 && !n.includes('\n')) briefName = n;
        }
        if (
          (!briefName || briefName === 'Untitled Brief' || briefName === 'Untitled brief') &&
          builderAnswers.decision
        ) {
          const derived = titleFromDecision(builderAnswers.decision);
          if (derived) briefName = derived;
        }
        if (!xploritUuid && bb.uuid) xploritUuid = bb.uuid;
      }

      if (versionsResponse?.success && versionsResponse?.data?.versions) {
        versionHistory = versionsResponse.data.versions.map((v: any) => ({
          uuid: v.uuid,
          version: v.version,
          title: v.title,
          date: new Date(v.created_at),
          alignmentPercent: v.alignment_percent ?? null,
          alignmentStatus: v.alignment_status || 'unknown',
          isCurrent: v.is_current,
          status: v.status || 'completed'
        }));

        hasCompletedVersions = versionHistory.some(v => v.status !== 'draft');
      } else {
        versionHistory = [];
        hasCompletedVersions = false;
      }

      if (response?.data?.response_summary) {
        briefGenerated = true;
        const rawSummary = response.data.response_summary;
        briefData = JSON.parse(rawSummary);
        originalBriefData = JSON.parse(rawSummary);
      }

      let briefTypeUuid: string | null =
        briefType?.uuid ?? summaryData?.xplorit_type_brief_uuid ?? null;

      const needTypeDetails =
        Boolean(briefTypeUuid) &&
        (!briefType ||
          briefType.has_scoring === undefined ||
          briefType.has_question_scoring === undefined);

      const currentVersion = versionHistory.find(v => v.isCurrent);

      const [currentVersionData, questionsData, briefTypeResponse]: [any, any, any] = await Promise.all([
        currentVersion
          ? api.getBriefVersion(briefId, currentVersion.version)
          : Promise.resolve(null),
        briefTypeUuid
          ? api.getBriefQuestions(briefTypeUuid)
          : Promise.resolve(null),
        needTypeDetails && briefTypeUuid
          ? api.getBriefType(briefTypeUuid)
          : Promise.resolve(null),
      ]);

      if (briefTypeResponse?.success && briefTypeResponse?.data) {
        briefType = { ...(briefType || {}), ...briefTypeResponse.data };
      }

      try {
        if (currentVersionData?.success && currentVersionData?.data?.answers) {
          briefAnswers = currentVersionData.data.answers;
          originalBriefAnswers = currentVersionData.data.answers;
        }

        if (questionsData?.success && questionsData?.data && Array.isArray(questionsData.data)) {
          briefQuestions = questionsData.data;
        }
      } catch (error) {
        console.error('Error loading answers:', error);
      }

      if (versionHistory.length > 0) {
        const latest = versionHistory.filter(v => v.status !== 'draft').find(v => v.isCurrent);
        currentViewingVersion = latest ? latest.version : null;

        activeTab = 'content';
      }

      try {
        if (draftResponse?.success && draftResponse?.data?.draft) {
          // Ensure all draft data is set atomically to prevent inconsistent rendering
          draftData = {
            hasActiveDraft: true,
            versionNumber: draftResponse.data.draft.version_number || 1, // Ensure versionNumber is never null
            createdAt: new Date(draftResponse.data.draft.created_at)
          };
        } else if (hasCompletedVersions || briefGenerated) {
          try {
            localStorage.removeItem(`localBriefAnswers_${briefId}`);
            localStorage.removeItem(`localBriefCurrentStep_${briefId}`);
          } catch (_) {}
          draftData = {
            hasActiveDraft: false,
            versionNumber: null,
            createdAt: null
          };
        } else {
          const draftAnswers = localStorage.getItem(`localBriefAnswers_${briefId}`);
          const draftStep = localStorage.getItem(`localBriefCurrentStep_${briefId}`);
          let hasSavedAnswers = false;
          if (draftAnswers) {
            try {
              const parsed = JSON.parse(draftAnswers);
              hasSavedAnswers = parsed && typeof parsed === 'object' && Object.keys(parsed).length > 0;
            } catch (_) {
              hasSavedAnswers = draftAnswers.trim() !== '' && draftAnswers !== '{}';
            }
          }

          if (hasSavedAnswers || draftStep) {
            draftData = {
              hasActiveDraft: true,
              versionNumber: 1,
              createdAt: new Date()
            };
          } else {
            draftData = {
              hasActiveDraft: false,
              versionNumber: null,
              createdAt: null
            };
          }
        }
      } catch (error) {
        console.error('[BriefView] Error fetching draft status:', error);
        if (hasCompletedVersions || briefGenerated) {
          draftData = {
            hasActiveDraft: false,
            versionNumber: null,
            createdAt: null
          };
        } else {
          const draftAnswers = localStorage.getItem(`localBriefAnswers_${briefId}`);
          const draftStep = localStorage.getItem(`localBriefCurrentStep_${briefId}`);
          if (draftAnswers || draftStep) {
            draftData = {
              hasActiveDraft: true,
              versionNumber: 1,
              createdAt: new Date()
            };
          } else {
            draftData = {
              hasActiveDraft: false,
              versionNumber: null,
              createdAt: null
            };
          }
        }
      }
    } catch (error) {
      console.error("Error loading brief:", error);
    } finally {
      spinner.hide();
    }
  }

  onMount(async () => {
    await loadBriefData();
    const storedJob = readStoredJobUuid();
    if (storedJob) startGenerationPolling(storedJob);
  });

  onDestroy(() => {
    if (generationPollTimer) { clearTimeout(generationPollTimer); generationPollTimer = null; }
  });
</script>

<div class="brief-view-dashboard" class:admin-mode={adminMode}>
  <!-- Top bar: back link + actions (back link hidden in admin mode) -->
  <div class="top-bar">
    {#if !adminMode}
      <button
        class="back-link"
        on:click={handleBackToBriefs}
        aria-label={builderFlow ? 'Back to Built-to-Brief' : 'Back to briefs list'}
      >
        <i class="angle left icon"></i>
        {builderFlow ? 'Back to Built-to-Brief' : 'Back to Briefs'}
      </button>
    {:else}
      <span></span>
    {/if}
    <div class="top-actions">
      {#if hasCompletedVersions}
        <button class="action-btn download-btn" on:click={handleDownloadPdf} title="Download PDF">
          <i class="download icon"></i>
          Download PDF
        </button>
        <button
          type="button"
          class="action-btn gapfinder-btn"
          on:click={handleReviewWithGapfinder}
          disabled={startingGapfinder}
          title="Run this brief through the GapFinder agent"
        >
          <i class="search icon" aria-hidden="true"></i>
          {startingGapfinder ? 'Starting…' : 'Review with GapFinder'}
        </button>
      {/if}
      {#if !adminMode && !showBuilderPaused}
        <button
          type="button"
          class="header-delete-brief"
          on:click={handleDiscardBrief}
          title="Permanently delete this brief"
        >
          <i class="trash icon" aria-hidden="true"></i>
          Delete brief
        </button>
      {/if}
    </div>
  </div>

  {#if isGenerating}
    <div class="generation-banner" role="status" aria-live="polite">
      <div class="ui active inline loader small"></div>
      <div class="generation-banner-text">
        <strong>Generating your brief…</strong>
        <span>This usually takes under a minute. We'll refresh automatically when it's ready.</span>
      </div>
    </div>
  {/if}

  <!-- Agent banner -->
  <div class="agent-banner" class:agent-banner-paused={showBuilderPaused}>
    <span class="agent-badge">BUILT-TO-BRIEF</span>
    <div class="agent-banner-right">
      <div class="agent-banner-title">
        {#if showBuilderPaused}
          CONSULTATION IN PROGRESS
        {:else}
          RESEARCH BRIEF
        {/if}
      </div>
      {#if versionHistory.length > 0}
        {@const bannerVersion = versionHistory.find(v => v.isCurrent || v.version === currentViewingVersion) || versionHistory[0]}
        {#if bannerVersion?.date}
          <div class="agent-banner-date">
            {bannerVersion.date.toLocaleDateString(navigator.language, { day: 'numeric', month: 'long', year: 'numeric' })}
          </div>
        {/if}
      {/if}
    </div>
  </div>

  <!-- Header Section -->
  <div class="brief-header">
    <div class="header-left">
      {#if editingBriefTitle}
        <div class="title-edit-wrapper-main">
          <input
            type="text"
            class="title-edit-input-main"
            bind:this={briefTitleInput}
            bind:value={editBriefTitleValue}
            on:keydown={(e) => {
              if (e.key === 'Enter') saveBriefTitle();
              if (e.key === 'Escape') cancelEditingBriefTitle();
            }}
            placeholder="Enter brief title..."
            maxlength="200"
          />
          <div class="title-edit-actions-main">
            <button class="title-save-btn-main" on:click={saveBriefTitle} title="Save">
              <i class="check icon"></i>
            </button>
            <button class="title-cancel-btn-main" on:click={cancelEditingBriefTitle} title="Cancel">
              <i class="times icon"></i>
            </button>
          </div>
        </div>
      {:else}
        <div class="brief-title-wrapper">
          <h1 class="brief-title">{briefName || 'Untitled Brief'}</h1>
          {#if !adminMode}
          <button class="title-edit-btn-main" on:click={startEditingBriefTitle} title="Edit title">
            <i class="edit icon"></i>
          </button>
          {/if}
          {#if (briefType?.has_scoring || builderFlow) && versionHistory.length > 0}
            {@const currentVersion = versionHistory.find(v => v.isCurrent || v.version === currentViewingVersion)}
            {#if currentVersion && currentVersion.alignmentPercent !== null && currentVersion.alignmentPercent !== undefined}
              <span class="title-score-badge {getAlignmentClass(currentVersion.alignmentStatus)}">
                <span class="title-score-value">{currentVersion.alignmentPercent}%</span>
                <span class="title-score-status">
                  {currentVersion.alignmentStatus === 'excellent' ? 'Launch!' :
                   currentVersion.alignmentStatus === 'good' ? 'This works' :
                   currentVersion.alignmentStatus === 'average' ? 'Getting closer' :
                   currentVersion.alignmentStatus === 'poor' ? 'Needs work' :
                   ''}
                </span>
              </span>
            {/if}
          {/if}
        </div>
      {/if}
    </div>
  </div>

  {#if showBuilderPaused}
    <BriefBuilderPaused
      answers={builderAnswers}
      {isGenerating}
      on:continue={handleResumeBrief}
      on:discard={handleDiscardBrief}
    />
  {:else if !adminMode && draftData.hasActiveDraft}
    <!-- Draft Alert (legacy rework drafts) - hidden in admin mode -->
    <div class="draft-alert">
      <div class="draft-icon">
        <i class="file alternate outline icon"></i>
      </div>
      <div class="draft-content">
        <div class="draft-header">
          <h3>Draft Version {draftData.versionNumber} in Progress</h3>
          {#if draftData.createdAt}
            <span class="draft-time">Started {formatRelativeTime(draftData.createdAt)}</span>
          {/if}
        </div>
        <p class="draft-description">
          {#if isGenerating}
            Your brief is still being generated. Resume will be available when generation finishes.
          {:else}
            You have an unfinished brief modification. You can resume where you left off or discard it to start fresh.
          {/if}
        </p>
      </div>
      <div class="draft-actions">
        <button
          type="button"
          class="btn-resume"
          disabled={isGenerating}
          aria-disabled={isGenerating}
          title={isGenerating ? 'Wait until generation finishes' : undefined}
          on:click={handleResumeBrief}
        >
          <i class="play icon"></i>
          Resume Draft
        </button>
        {#if hasCompletedVersions}
          <button type="button" class="btn-discard" on:click={handleDiscardDraft}>
            <i class="trash icon"></i>
            Discard draft
          </button>
        {:else}
          <button type="button" class="btn-discard" on:click={handleDiscardBrief}>
            <i class="trash icon"></i>
            Delete brief
          </button>
        {/if}
      </div>
    </div>
  {/if}

  <!-- Show content/tabs/history only if brief has completed versions -->
  {#if hasCompletedVersions}
    <!-- Main Content Grid (full width in admin mode, no version history) -->
    <div class="content-grid" class:content-grid-admin={adminMode}>
    <!-- Main Content Area with Tabs -->
    <div class="content-main">
      <!-- Tabs Navigation -->
      <div class="tabs-nav">
        <button 
          class="tab-btn {activeTab === 'content' ? 'active' : ''}"
          on:click={() => activeTab = 'content'}
        >
          <i class="file text icon"></i>
          Brief Content
        </button>
        <button 
          class="tab-btn {activeTab === 'answers' ? 'active' : ''}"
          on:click={() => activeTab = 'answers'}
        >
          <i class="question circle icon"></i>
          {adminMode ? 'Their Answers' : 'Your Answers'}
          {#if builderFlow}
            <span class="tab-badge">9</span>
          {:else if uniqueBriefAnswers.length > 0}
            <span class="tab-badge">{uniqueBriefAnswers.length}</span>
          {/if}
        </button>
      </div>

      <!-- Tab Content -->
      <div class="tab-content">
        {#if activeTab === 'content'}
          <div class="content-card">
            {#if briefGenerated && briefData?.methodology_alignment === 'declined' && briefData?.methodology_decline_reason}
              <div class="methodology-notice" role="status">
                <i class="exclamation triangle icon" aria-hidden="true"></i>
                <div>
                  <h3 class="methodology-notice-title">Methodology note</h3>
                  <p class="methodology-notice-body">{briefData.methodology_decline_reason}</p>
                </div>
              </div>
            {/if}

            {#if briefGenerated && researchSections.length > 0}
              <div class="brief-sections">
                {#each researchSections as section (section.name)}
                  {@const timeline =
                    Array.isArray(section.timeline) && section.timeline.length > 0
                      ? section.timeline
                      : /^timing\b/i.test(String(section.name || '')) && Array.isArray(section.text)
                        ? parseTimingLines(section.text)
                        : []}
                  <div class="brief-section" class:is-problem={isProblem(section)}>
                    {#if isProblem(section)}
                      <p class="doc-kicker">The problem, as we see it</p>
                      {#each sectionLines(section) as line, lineIndex (lineIndex)}
                        <p class="problem-question">{@html sanitizeHtml(formatBriefLine(stripLeadNumber(line)))}</p>
                      {/each}
                    {:else}
                    <h3 class="section-title">{displayLabel(section.name)}</h3>
                    {#if isFindOut(section)}
                      <p class="doc-sub">The questions this decision hinges on</p>
                    {:else if isMethod(section)}
                      <p class="doc-sub">How we'd answer it</p>
                    {/if}
                    <div class="section-content">
                      {#if isFindOut(section)}
                        {#if section.intro}
                          <p>{@html sanitizeHtml(formatBriefLine(String(section.intro)))}</p>
                        {/if}
                        {#each areaList(section) as area, areaIndex (area.question || areaIndex)}
                          <article class="findout-area">
                            <div class="findout-head">
                              <span class="num-circle">{areaIndex + 1}</span>
                              <p class="findout-label">{area.title}</p>
                            </div>
                            <h4 class="findout-question">{area.question}</h4>
                            {#if area.why_it_matters}
                              <p class="findout-why"><span>Why it matters:</span> {area.why_it_matters}</p>
                            {/if}
                          </article>
                        {/each}
                        {#if leftOutList(section).length > 0}
                          <aside class="findout-left-out">
                            <p class="findout-label">What this brief deliberately leaves out</p>
                            <ul>
                              {#each leftOutList(section) as item (item.item)}
                                <li><strong>{item.item}</strong> {item.reason}</li>
                              {/each}
                            </ul>
                          </aside>
                        {/if}
                      {:else if isMethod(section)}
                        {#if section.approach}
                          <p class="method-badge">{section.approach}</p>
                        {/if}
                        {#if section.recommendation}
                          <div class="method-reco">
                            <p class="method-reco-label">Our recommendation</p>
                            <p class="method-lead">{section.recommendation}</p>
                          </div>
                        {/if}
                        {#if section.why_this_approach}
                          <h4 class="method-subhead">Why this approach</h4>
                          <p>{@html sanitizeHtml(formatBriefLine(String(section.why_this_approach)))}</p>
                        {/if}
                        {#if methodSteps(section).length > 0}
                          <h4 class="method-subhead">How it will run</h4>
                          <ol class="method-steps">
                            {#each methodSteps(section) as step, stepIndex (step.name || stepIndex)}
                              <li>
                                <div class="method-step-head">
                                  <span class="num-circle">{stepIndex + 1}</span>
                                  <span class="method-step-name">{step.name}</span>
                                  {#if step.timing}
                                    <span class="method-timing">{step.timing}</span>
                                  {/if}
                                </div>
                                {#if step.what_we_do}
                                  <p>{step.what_we_do}</p>
                                {/if}
                                {#if step.what_you_get}
                                  <p class="method-have"><span>You'll have:</span> {step.what_you_get}</p>
                                {/if}
                              </li>
                            {/each}
                          </ol>
                        {/if}
                        {#if section.who_we_speak_to}
                          <h4 class="method-subhead">Who we'll speak to</h4>
                          <p>{@html sanitizeHtml(formatBriefLine(String(section.who_we_speak_to)))}</p>
                        {/if}
                        {#if section.confidence}
                          <h4 class="method-subhead">How much weight it will bear</h4>
                          <p>{@html sanitizeHtml(formatBriefLine(String(section.confidence)))}</p>
                        {/if}
                        {#if section.options?.faster || section.options?.fuller}
                          <h4 class="method-subhead">If you need to flex it</h4>
                          <div class="method-options">
                            {#if section.options?.faster}
                              <article>
                                <p class="findout-label">{section.options.faster.label}</p>
                                <p>{section.options.faster.detail}</p>
                              </article>
                            {/if}
                            {#if section.options?.fuller}
                              <article>
                                <p class="findout-label">{section.options.fuller.label}</p>
                                <p>{section.options.fuller.detail}</p>
                              </article>
                            {/if}
                          </div>
                        {/if}
                        {#if Array.isArray(section.watch_outs) && section.watch_outs.length > 0}
                          <h4 class="method-subhead">Watch-outs</h4>
                          <ul>
                            {#each section.watch_outs as watch (watch)}
                              <li>{watch}</li>
                            {/each}
                          </ul>
                        {/if}
                        {#if section.timing}
                          <h4 class="method-subhead">Timing</h4>
                          <p>{@html sanitizeHtml(formatBriefLine(String(section.timing)))}</p>
                        {/if}
                      {:else if namedCards(section).length > 0}
                        {#if section.intro}
                          <p>{@html sanitizeHtml(formatBriefLine(String(section.intro)))}</p>
                        {/if}
                        <div class="named-cards" class:named-stack={/framework/i.test(String(section.name || ""))}>
                          {#each namedCards(section) as card (card.title)}
                            <article>
                              <h4>{card.title}</h4>
                              <p>{@html sanitizeHtml(formatBriefLine(card.body))}</p>
                            </article>
                          {/each}
                        </div>
                      {:else if section.themes && Array.isArray(section.themes)}
                        <!-- New structured themes format for "What We're Exploring" -->
                        {#each section.themes as theme, themeIndex}
                          <div class="theme-group">
                            <h4 class="theme-title">{themeIndex + 1}. {theme.title}</h4>
                            {#if theme.questions && Array.isArray(theme.questions)}
                              <ol class="theme-questions">
                                {#each theme.questions as question}
                                  <li>{question}</li>
                                {/each}
                              </ol>
                            {/if}
                          </div>
                        {/each}
                      {:else if timeline.length > 0}
                        <ol class="timing-timeline" aria-label="Research timing">
                          {#each timeline as row, i}
                            <li class="timing-row">
                              <span class="timing-rail" aria-hidden="true">
                                <span class="timing-dot"></span>
                                {#if i < timeline.length - 1}
                                  <span class="timing-stem"></span>
                                {/if}
                              </span>
                              <div class="timing-body">
                                <span class="timing-phase">{row.phase}</span>
                                <p class="timing-activity">{@html sanitizeHtml(formatBriefLine(row.activity))}</p>
                              </div>
                            </li>
                          {/each}
                        </ol>
                      {:else if isNumberedList(section) && section.text && Array.isArray(section.text)}
                        <ol class="numbered-list">
                          {#each section.text as line, lineIndex (lineIndex)}
                            <li>
                              <span class="num-circle">{lineIndex + 1}</span>
                              <span>{@html sanitizeHtml(formatBriefLine(stripLeadNumber(String(line))))}</span>
                            </li>
                          {/each}
                        </ol>
                      {:else if section.text && Array.isArray(section.text)}
                        {#each section.text as line}
                          <p>{@html sanitizeHtml(formatBriefLine(line))}</p>
                        {/each}
                      {/if}
                    </div>
                    {/if}
                  </div>
                {/each}
              </div>

              <!-- Proposal CTA Section (hidden in admin mode) -->
              {#if !adminMode}
              <div class="proposal-cta-section">
                <div class="proposal-cta-content">
                  <div class="proposal-cta-icon">
                    <i class="paper plane outline icon"></i>
                  </div>
                  <div class="proposal-cta-text">
                    <h3>Ready to take the next step?</h3>
                    <p>Would you like us to submit a proposal with recommendations of the research methodology to use? You can choose to engage us or others.</p>
                  </div>
                  <button class="proposal-cta-btn" on:click={openProposalModal}>
                    <i class="envelope icon"></i>
                    Request a Proposal
                  </button>
                </div>
              </div>
              {/if}
            {:else}
              <div class="incomplete-brief">
                <div class="incomplete-icon">
                  <i class="hourglass half icon"></i>
                </div>
                <div class="incomplete-content">
                  <h3>Brief Not Yet Completed</h3>
                  <p>
                    {#if isGenerating}
                      Your brief is still being generated. You can resume the questionnaire after generation finishes, or discard if you no longer need it.
                    {:else}
                      This brief hasn't been completed yet. You can resume where you left off or discard it if you no longer need it.
                    {/if}
                  </p>
                  <div class="incomplete-actions">
                    <button
                      type="button"
                      class="action-btn resume-primary"
                      disabled={isGenerating}
                      aria-disabled={isGenerating}
                      title={isGenerating ? 'Wait until generation finishes' : undefined}
                      on:click={handleResumeBrief}
                    >
                      <i class="play icon"></i>
                      Resume Brief
                    </button>
                    <button class="action-btn discard-secondary" on:click={handleDiscardBrief}>
                      <i class="trash icon"></i>
                      Discard Brief
                    </button>
                  </div>
                </div>
              </div>
            {/if}
          </div>
        {:else if activeTab === 'answers'}
          <div class="answers-card">
            {#if builderFlow}
              {#if builderAnswers.decision || builderAnswers.territory || uniqueBriefAnswers.length > 0}
                <BriefBuilderAnswersPanel
                  answers={builderAnswers}
                  stepScores={builderStepScores}
                />
              {:else}
                <div class="no-answers">
                  <div class="no-answers-icon">
                    <i class="history icon"></i>
                  </div>
                  <h3>No answers yet</h3>
                  <p>Consultation answers will show here once you’ve answered a few questions.</p>
                </div>
              {/if}
            {:else if uniqueBriefAnswers.length > 0}
              <div class="answers-list-full">
                {#each uniqueBriefAnswers as answer, index}
                  {@const question = briefQuestions.find(q => q.uuid === answer.xplorit_type_brief_question_uuid)}
                  {@const scoreClass = answer.score >= 76 ? 'score-excellent' : answer.score >= 51 ? 'score-good' : answer.score >= 26 ? 'score-average' : 'score-poor'}
                  {@const answerText = answer.brief_type_answer || answer.answer || 'No answer provided'}
                  {@const fieldRows = parseLabeledAnswerFields(answerText, fieldLabelsForQuestion(question))}
                  <div class="answer-item-full">
                    <div class="answer-question-full">
                      <span class="question-number">Q{index + 1}</span>
                      <span class="question-text-full">{question?.question_content || answer.question || 'Question'}</span>
                      {#if briefType?.has_question_scoring && answer.score !== null && answer.score !== undefined}
                        <span class="question-score {scoreClass}">
                          {answer.score}%
                        </span>
                      {/if}
                    </div>
                    <div class="answer-text-full">
                      {#if fieldRows.length > 0}
                        <div class="answer-fields">
                          {#each fieldRows as field (field.label)}
                            <div class="answer-field">
                              <div class="answer-field-label">{field.label}</div>
                              <div class="answer-field-value">{field.value || "I don't know"}</div>
                            </div>
                          {/each}
                        </div>
                      {:else}
                        {answerText}
                      {/if}
                    </div>
                    {#if briefType?.has_question_scoring && answer.score !== null && answer.score !== undefined}
                      <div class="answer-feedback {scoreClass}">
                        <i class="lightbulb outline icon"></i>
                        <div class="feedback-content">
                          {#if answer.feedback}
                            <p>{answer.feedback}</p>
                          {:else}
                            <p class="feedback-fallback">
                              {#if answer.score >= 76}
                                Great job on this answer! Your response is well-structured and comprehensive.
                              {:else if answer.score >= 51}
                                This answer is solid but could benefit from more specific details or examples.
                              {:else if answer.score >= 26}
                                Consider expanding this answer with more context, specifics, or measurable criteria.
                              {:else}
                                This answer needs significant improvement. Try adding more detail, context, and specific information.
                              {/if}
                            </p>
                          {/if}
                        </div>
                      </div>
                    {/if}
                  </div>
                {/each}
              </div>
            {:else}
              <div class="no-answers">
                <div class="no-answers-icon">
                  <i class="history icon"></i>
                </div>
                <h3>No Answer Snapshot</h3>
                <p>This version was created before answer snapshots were implemented. Answers were not recorded with this version.</p>
                {#if currentViewingVersion !== null}
                  {@const latestVersion = versionHistory.find(v => v.isCurrent)}
                  {#if latestVersion && currentViewingVersion !== latestVersion.version}
                    <button class="view-latest-btn" on:click={handleViewLatest}>
                      <i class="eye icon"></i>
                      View Latest Version for Answers
                    </button>
                  {/if}
                {/if}
              </div>
            {/if}
          </div>
        {/if}
      </div>
    </div>

    <!-- Version History Section (hidden in admin mode) -->
    {#if !adminMode}
    <div class="version-card">
      <div class="card-header">
        <h2>Version History</h2>
        <span class="version-count">{versionHistory.filter(v => v.status !== 'draft').length} versions</span>
      </div>
      
      <div class="version-list">
        {#each versionHistory.filter(v => v.status !== 'draft') as version, index}
          <div class="version-item {currentViewingVersion === version.version ? 'viewing' : ''}">
            <div class="version-main" 
              role="button"
              tabindex="0"
              on:click={() => handleViewVersion(version.version)}
              on:keydown={(e) => e.key === 'Enter' && handleViewVersion(version.version)}
            >
              <div class="version-header">
                <div class="version-info">
                  <span class="version-number">v{version.version}</span>
                  {#if editingVersionTitle === version.version}
                    <div class="title-edit-wrapper">
                      <input
                        type="text"
                        class="title-edit-input"
                        bind:value={editTitleValue}
                        on:click|stopPropagation
                        on:keydown={(e) => {
                          e.stopPropagation();
                          if (e.key === 'Enter') saveVersionTitle(version, e);
                          if (e.key === 'Escape') cancelEditingTitle(e);
                        }}
                        placeholder="Enter version title..."
                        maxlength="100"
                      />
                      <div class="title-edit-actions">
                        <button class="title-save-btn" on:click={(e) => saveVersionTitle(version, e)} title="Save">
                          <i class="check icon"></i>
                        </button>
                        <button class="title-cancel-btn" on:click={cancelEditingTitle} title="Cancel">
                          <i class="times icon"></i>
                        </button>
                      </div>
                    </div>
                  {:else}
                    <div class="version-title-display">
                      {#if version.title}
                        <span class="version-title">{version.title}</span>
                      {:else}
                        <span class="version-title-placeholder">No title</span>
                      {/if}
                      <button class="title-edit-btn" on:click={(e) => startEditingTitle(version, e)} title="Edit title">
                        <i class="edit icon"></i>
                      </button>
                    </div>
                  {/if}
                </div>
                <div class="version-date">
                  <i class="calendar outline icon"></i>
                  {formatDateTime(version.date)}
                </div>
              </div>
              
              <div class="version-alignment">
                <div class="alignment-label">Alignment</div>
                <div class="alignment-details">
                  <div class="alignment-percent">{version.alignmentPercent != null ? version.alignmentPercent + '%' : '—'}</div>
                  <span class="alignment-status {getAlignmentClass(version.alignmentStatus)}">
                    <i class="{getAlignmentIcon(version.alignmentStatus)} icon"></i>
                    {version.alignmentPercent != null 
                      ? (version.alignmentStatus === 'excellent' ? 'Launch!' : version.alignmentStatus === 'good' ? 'This works' : version.alignmentStatus === 'average' ? 'Getting closer' : version.alignmentStatus === 'poor' ? 'Needs work' : version.alignmentStatus.charAt(0).toUpperCase() + version.alignmentStatus.slice(1))
                      : 'Not scored'}
                  </span>
                </div>
              </div>
            </div>
            
            {#if !adminMode && !draftData.hasActiveDraft && currentViewingVersion === version.version}
              <button 
                class="version-modify-btn" 
                on:click|stopPropagation={() => handleModifyVersion(version.version)}
                title="Create new version based on v{version.version}"
              >
                <i class="redo icon"></i>
                Rework This Version
              </button>
            {/if}
          </div>
        {/each}
      </div>
    </div>
    {/if}
  </div>
  {:else if !showBuilderPaused && !hasCompletedVersions && !draftData.hasActiveDraft}
    <!-- Incomplete Brief State (legacy) - No completed versions and no draft -->
    <div class="incomplete-brief-state">
      <div class="incomplete-brief">
        <div class="incomplete-icon">
          <i class="hourglass half icon"></i>
        </div>
        <div class="incomplete-content">
          <h3>Brief Not Yet Completed</h3>
          <p>
            {#if isGenerating}
              Your brief is still being generated. You can resume the questionnaire after generation finishes, or discard if you no longer need it.
            {:else}
              This brief hasn't been completed yet. You can resume where you left off or discard it if you no longer need it.
            {/if}
          </p>
          <div class="incomplete-actions">
            <button
              type="button"
              class="action-btn resume-primary"
              disabled={isGenerating}
              aria-disabled={isGenerating}
              title={isGenerating ? 'Wait until generation finishes' : undefined}
              on:click={handleResumeBrief}
            >
              <i class="play icon"></i>
              Resume Brief
            </button>
            <button class="action-btn discard-secondary" on:click={handleDiscardBrief}>
              <i class="trash icon"></i>
              Discard Brief
            </button>
          </div>
        </div>
      </div>
    </div>
  {/if}
</div>

<!-- Discard Draft Confirmation Dialog -->
<ConfirmDialog
  bind:show={showDiscardDraftDialog}
  title={hasCompletedVersions ? "Discard Draft?" : "Discard Brief?"}
  message={hasCompletedVersions 
    ? "Are you sure you want to discard this draft? All progress will be lost and cannot be recovered."
    : "Are you sure you want to discard this brief? Since there are no completed versions, this will permanently delete the entire brief and all associated data. This action cannot be undone."}
  confirmText={hasCompletedVersions ? "Discard Draft" : "Delete Brief"}
  cancelText={hasCompletedVersions ? "Keep Draft" : "Cancel"}
  type="danger"
  icon="trash"
  on:confirm={confirmDiscardDraft}
  on:cancel={cancelDiscardDraft}
/>

<!-- Delete brief confirmation (header + draft-only banner) -->
<ConfirmDialog
  bind:show={showDiscardBriefDialog}
  title="Delete brief?"
  message="This will permanently delete this brief and all associated data. This action cannot be undone. Are you sure?"
  confirmText="Delete brief"
  cancelText="Cancel"
  type="danger"
  icon="trash"
  on:confirm={confirmDiscardBrief}
  on:cancel={cancelDiscardBrief}
/>

<!-- Proposal Request Modal -->
{#if showProposalModal}
  <!-- svelte-ignore a11y-click-events-have-key-events a11y-no-noninteractive-element-interactions -->
  <div class="modal-overlay" on:click={closeProposalModal} on:keydown={(e) => e.key === 'Escape' && closeProposalModal()} role="dialog" aria-modal="true" aria-labelledby="proposal-modal-title" tabindex="-1">
    <!-- svelte-ignore a11y-click-events-have-key-events a11y-no-noninteractive-element-interactions -->
    <div class="modal-content proposal-modal" on:click|stopPropagation role="document">
      {#if proposalSubmitted}
        <!-- Success State -->
        <div class="proposal-success">
          <div class="success-icon">
            <i class="check circle icon"></i>
          </div>
          <h3>Request Submitted!</h3>
          <p>Thank you for your interest. We'll be in touch soon with a tailored proposal for your research needs.</p>
          <button class="btn-ok" on:click={closeProposalModal}>OK</button>
        </div>
      {:else}
        <!-- Form State -->
        <div class="modal-header">
          <h3 id="proposal-modal-title">Request a Proposal</h3>
          <button class="modal-close" on:click={closeProposalModal} aria-label="Close modal">
            <i class="times icon"></i>
          </button>
        </div>
        <div class="modal-body">
          <p class="modal-description">
            We'll send you a proposal with specific research methodology recommendations tailored to your brief.
          </p>
          <form on:submit|preventDefault={handleSubmitProposal}>
            <div class="form-group">
              <label for="phone">Phone Number <span class="optional">(optional)</span></label>
              <input 
                type="tel" 
                id="phone" 
                bind:value={proposalForm.phone} 
                placeholder="Enter your phone number"
              />
            </div>
            <div class="form-group">
              <label for="notes">Additional Notes <span class="optional">(optional)</span></label>
              <textarea 
                id="notes" 
                bind:value={proposalForm.notes} 
                placeholder="Any specific requirements or questions you'd like addressed in the proposal?"
                rows="4"
              ></textarea>
            </div>
            <div class="modal-actions">
              <button type="button" class="btn-cancel" on:click={closeProposalModal}>
                Cancel
              </button>
              <button 
                type="submit" 
                class="btn-submit"
                disabled={proposalSubmitting}
              >
                {#if proposalSubmitting}
                  <i class="spinner loading icon"></i>
                  Submitting...
                {:else}
                  <i class="paper plane icon"></i>
                  Submit Request
                {/if}
              </button>
            </div>
          </form>
        </div>
      {/if}
    </div>
  </div>
{/if}

<style>
  .brief-view-dashboard {
    padding: 0;
  }

  /* Generation banner (appears while a brief is being generated in the background) */
  .generation-banner {
    display: flex;
    align-items: center;
    gap: 1rem;
    padding: 1rem 1.25rem;
    margin-bottom: 1.5rem;
    background: linear-gradient(135deg, rgba(5, 108, 242, 0.08) 0%, rgba(5, 108, 242, 0.03) 100%);
    border: 1px solid rgba(5, 108, 242, 0.25);
    border-radius: 12px;
    color: var(--grey);
  }

  .generation-banner-text {
    display: flex;
    flex-direction: column;
    gap: 0.125rem;
    line-height: 1.4;
  }

  .generation-banner-text strong {
    color: var(--primary);
    font-weight: 600;
  }

  .generation-banner-text span {
    font-size: 0.875rem;
    color: var(--grey-secondary);
  }

  /* Top bar */
  .top-bar {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 1rem;
    flex-wrap: wrap;
    margin-bottom: 1.5rem;
  }

  .top-actions {
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem;
  }

  .back-link {
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    color: var(--grey-secondary);
    background: none;
    border: none;
    cursor: pointer;
    font-size: 1rem;
    font-weight: 400;
    padding: 0.5rem 0;
    transition: color 0.2s ease;
  }

  .back-link:hover {
    color: var(--primary);
  }

  /* Agent banner */
  .agent-banner {
    display: flex;
    justify-content: space-between;
    align-items: center;
    border-bottom: 3px solid var(--accent-orange);
    padding-bottom: 12px;
    margin-bottom: 1.5rem;
    gap: 1rem;
    flex-wrap: wrap;
  }

  .agent-badge {
    display: inline-block;
    background: var(--accent-orange);
    color: var(--white);
    font-weight: 700;
    letter-spacing: 3px;
    padding: 8px 20px;
    border-radius: 8px;
    font-size: 13px;
  }

  .agent-banner-right { text-align: right; }

  .agent-banner-title {
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 2px;
    color: var(--grey-secondary);
  }

  .agent-banner-date {
    font-size: 12px;
    color: var(--grey-secondary);
    margin-top: 2px;
  }

  .agent-banner-paused .agent-banner-title {
    color: var(--accent-orange-ink);
  }

  /* Header Section */
  .brief-header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    margin-bottom: 2rem;
    gap: 2rem;
  }

  .header-left {
    flex: 1;
  }

  .brief-title-wrapper {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    flex-wrap: wrap;
  }

  .title-score-badge {
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.375rem 0.75rem;
    border-radius: 9999px;
    font-size: 0.875rem;
    font-weight: 500;
    margin-left: 0.5rem;
  }

  .title-score-badge.alignment-excellent {
    background: #d4f4dd;
    color: #0f5132;
  }

  .title-score-badge.alignment-good {
    background: #cfe2ff;
    color: #084298;
  }

  .title-score-badge.alignment-average {
    background: #fff3cd;
    color: #664d03;
  }

  .title-score-badge.alignment-poor {
    background: #f8d7da;
    color: #842029;
  }

  .title-score-value {
    font-weight: 600;
  }

  .title-score-status {
    font-weight: 400;
    opacity: 0.9;
  }

  .brief-title {
    font-size: 2rem;
    font-weight: 500;
    color: var(--grey);
    margin: 0 0 0.75rem 0;
    line-height: 1.2;
  }

  .title-edit-btn-main {
    background: transparent;
    border: none;
    color: var(--grey-secondary);
    cursor: pointer;
    padding: 0.5rem;
    opacity: 0.7;
    transition: all 0.2s ease;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 0.75rem;
  }

  .brief-title-wrapper:hover .title-edit-btn-main {
    opacity: 1;
  }

  .title-edit-btn-main:hover {
    color: var(--primary);
  }

  .title-edit-btn-main i {
    font-size: 1rem;
  }

  .title-edit-wrapper-main {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    margin-bottom: 0.75rem;
    width: 100%;
    max-width: 600px;
  }

  .title-edit-input-main {
    flex: 1;
    padding: 0.75rem 1rem;
    border: 2px solid var(--primary);
    border-radius: 6px;
    font-size: 2rem;
    font-weight: 500;
    color: var(--grey);
    background: white;
    font-family: "Poppins", sans-serif;
  }

  .title-edit-input-main:focus {
    outline: none;
    box-shadow: 0 0 0 3px rgba(5, 108, 242, 0.1);
  }

  .title-edit-actions-main {
    display: flex;
    gap: 0.5rem;
  }

  .title-save-btn-main,
  .title-cancel-btn-main {
    padding: 0.75rem;
    border: none;
    border-radius: 6px;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.2s ease;
    width: 44px;
    height: 44px;
  }

  .title-save-btn-main {
    background: #45c496;
    color: white;
  }

  .title-save-btn-main:hover {
    background: #3ab386;
  }

  .title-cancel-btn-main {
    background: var(--greyish);
    color: var(--grey);
  }

  .title-cancel-btn-main:hover {
    background: var(--grey-secondary);
    color: white;
  }

  .title-save-btn-main i,
  .title-cancel-btn-main i {
    font-size: 1.125rem;
    margin: 0;
  }

  .download-btn {
    background: var(--primary);
    color: white;
  }

  .download-btn:hover {
    background: var(--darkprimary);
    transform: translateY(-1px);
    box-shadow: 2px 4px 6px rgba(0, 0, 0, 0.25);
  }

  .gapfinder-btn {
    background: var(--white);
    color: var(--grey);
    border: 1px solid var(--border-color);
  }

  .gapfinder-btn:hover:not(:disabled) {
    background: var(--greyish);
    transform: translateY(-1px);
    box-shadow: 2px 4px 6px rgba(0, 0, 0, 0.1);
  }

  .gapfinder-btn:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  .title-skeleton {
    display: flex;
    align-items: center;
    gap: 1rem;
    color: var(--grey-secondary);
    font-size: 1.125rem;
    margin-bottom: 0.75rem;
  }

  .header-actions {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: flex-end;
    gap: 0.75rem;
  }

  .header-delete-brief {
    display: inline-flex;
    align-items: center;
    gap: 0.45rem;
    padding: 0.5rem 0.75rem;
    margin: 0;
    border: none;
    border-radius: 6px;
    background: transparent;
    color: var(--grey-secondary);
    font: inherit;
    font-size: 0.9rem;
    font-weight: 500;
    cursor: pointer;
    transition: color 0.15s ease, background 0.15s ease;
    white-space: nowrap;
  }

  .header-delete-brief :global(i.icon) {
    margin: 0;
    font-size: 1rem;
    opacity: 0.9;
  }

  .header-delete-brief:hover {
    color: #842029;
    background: rgba(248, 215, 218, 0.35);
  }

  .header-delete-brief:focus-visible {
    outline: none;
    box-shadow: var(--focus-ring, 0 0 0 3px rgba(5, 108, 242, 0.35));
  }

  .action-btn {
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.75rem 1.25rem;
    border: 0;
    border-radius: 6px;
    font-weight: 500;
    font-size: 0.95rem;
    cursor: pointer;
    transition: all 0.2s ease;
    box-shadow: 1px 2px 3px rgba(0, 0, 0, 0.2);
    white-space: nowrap;
  }

  .action-btn.primary {
    background: #d91b42;
    color: white;
  }

  .action-btn.primary:hover {
    background: #c01638;
    transform: translateY(-1px);
    box-shadow: 2px 4px 6px rgba(0, 0, 0, 0.25);
  }

  .action-btn.resume-primary {
    background: var(--primary);
    color: var(--white);
  }

  .action-btn.resume-primary:hover {
    background: var(--darkprimary);
    transform: translateY(-1px);
    box-shadow: var(--shadow-cta);
  }

  .action-btn.resume-primary:disabled {
    background: var(--background-color-disabled);
    color: var(--color-disabled);
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
    opacity: 0.9;
  }

  .action-btn.resume-primary:disabled:hover {
    background: var(--background-color-disabled);
    transform: none;
    box-shadow: none;
  }

  .action-btn.discard-secondary {
    background: var(--white);
    color: var(--status-danger-ink);
    border: 2px solid var(--status-danger);
  }

  .action-btn.discard-secondary:hover {
    background: var(--status-danger);
    color: var(--white);
    transform: translateY(-1px);
    box-shadow: var(--shadow-cta);
  }

  /* Draft Alert */
  .draft-alert {
    background: linear-gradient(135deg, #fff9e6 0%, #fffbf0 100%);
    border: 2px solid #ffc107;
    border-radius: 12px;
    padding: 1.5rem;
    margin-bottom: 2rem;
    display: flex;
    gap: 1.5rem;
    align-items: flex-start;
    box-shadow: 0 2px 8px rgba(255, 193, 7, 0.2);
  }

  .draft-icon {
    font-size: 2.5rem;
    color: #f57c00;
  }

  .draft-content {
    flex: 1;
  }

  .draft-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 0.75rem;
  }

  .draft-header h3 {
    margin: 0;
    font-size: 1.1rem;
    color: var(--grey);
    font-weight: 500;
  }

  .draft-time {
    font-size: 0.85rem;
    color: var(--grey-secondary);
  }

  .draft-progress {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }

  .progress-info {
    display: flex;
    justify-content: space-between;
    font-size: 0.9rem;
    color: var(--grey);
  }

  .progress-percent {
    font-weight: 500;
    color: #f57c00;
  }

  .progress-bar {
    height: 8px;
    background: rgba(0, 0, 0, 0.1);
    border-radius: 4px;
    overflow: hidden;
  }

  .progress-fill {
    height: 100%;
    background: linear-gradient(90deg, #ffc107 0%, #f57c00 100%);
    transition: width 0.3s ease;
  }

  .draft-actions {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    flex-shrink: 0;
    align-self: stretch;
    min-width: fit-content;
  }

  .btn-resume,
  .btn-discard {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 0.5rem;
    padding: 0.625rem 1rem;
    border: 0;
    border-radius: 6px;
    font-weight: 500;
    font-size: 0.9rem;
    cursor: pointer;
    transition: all 0.2s ease;
    white-space: nowrap;
  }

  .btn-resume {
    background: #45c496;
    color: white;
    box-shadow: 1px 2px 3px rgba(0, 0, 0, 0.2);
  }

  .btn-resume:hover {
    background: #3ab085;
    transform: translateY(-1px);
  }

  .btn-resume:disabled {
    background: #a8d4c4;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
    opacity: 0.9;
  }

  .btn-resume:disabled:hover {
    background: #a8d4c4;
    transform: none;
  }

  .btn-discard {
    background: transparent;
    color: var(--grey-secondary);
    border: 1px solid var(--border-color);
  }

  .btn-discard:hover {
    background: #f8d7da;
    color: #842029;
    border-color: #842029;
  }

  /* Grid Layout */
  .content-grid {
    display: grid;
    grid-template-columns: 2fr 1fr;
    gap: 2rem;
    align-items: start;
  }

  .content-grid.content-grid-admin {
    grid-template-columns: 1fr;
  }
  
  .content-main {
    display: flex;
    flex-direction: column;
    gap: 0;
  }
  
  /* Tabs Navigation */
  .tabs-nav {
    display: flex;
    gap: 0;
    background: var(--white);
    border-radius: 12px 12px 0 0;
    box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1);
    overflow: hidden;
  }
  
  .tab-btn {
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.5rem;
    padding: 1.25rem 1.5rem;
    background: var(--greyish);
    border: none;
    border-bottom: 3px solid transparent;
    font-size: 1rem;
    font-weight: 500;
    color: var(--grey-secondary);
    cursor: pointer;
    transition: all 0.2s ease;
    position: relative;
  }
  
  .tab-btn:hover {
    background: #e9ecef;
    color: var(--grey);
  }
  
  .tab-btn.active {
    background: white;
    color: var(--primary);
    border-bottom-color: var(--primary);
  }
  
  .tab-btn i {
    font-size: 1.125rem;
  }
  
  .tab-badge {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 24px;
    height: 24px;
    padding: 0 0.5rem;
    background: var(--primary);
    color: white;
    border-radius: 12px;
    font-size: 0.75rem;
    font-weight: 600;
    margin-left: 0.25rem;
  }
  
  .tab-btn.active .tab-badge {
    background: linear-gradient(135deg, var(--primary) 0%, var(--secondary) 100%);
  }
  
  /* Tab Content */
  .tab-content {
    background: var(--white);
    border-radius: 0 0 12px 12px;
    box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1);
  }

  /* Cards */
  .content-card,
  .version-card,
  .answers-card {
    background: transparent;
  }

  .version-card {
    background: var(--white);
    border-radius: 12px;
    box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1);
    overflow: hidden;
  }

  .card-header {
    padding: 1.5rem 2rem;
    border-bottom: 1px solid var(--greyish);
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  .card-header h2 {
    font-size: 1.25rem;
    font-weight: 500;
    color: var(--grey);
    margin: 0;
  }

  .methodology-notice {
    display: flex;
    gap: var(--space-4);
    align-items: flex-start;
    margin: var(--space-5) var(--space-6) 0;
    padding: var(--space-4) var(--space-5);
    background: var(--status-warn-soft);
    border: 1px solid var(--status-warn);
    border-radius: var(--radius-md);
    color: var(--grey);
  }

  .methodology-notice .icon {
    color: var(--status-warn-ink);
    font-size: 1.25rem;
    margin-top: 0.125rem;
  }

  .methodology-notice-title {
    margin: 0 0 var(--space-2);
    font-size: 1rem;
    font-weight: 500;
    color: var(--status-warn-ink);
  }

  .methodology-notice-body {
    margin: 0;
    line-height: 1.5;
  }

  /* Brief document — editorial layout in Xplorit colours */
  .brief-sections {
    padding: 2rem 2.25rem 2.5rem;
    display: flex;
    flex-direction: column;
    gap: 0;
    background: var(--white);
    color: var(--grey);
    border-radius: 0 0 12px 12px;
  }

  .brief-section {
    background: transparent;
    border: 0;
    border-bottom: 1px solid var(--border-color);
    border-radius: 0;
    padding: 1.75rem 0;
    position: relative;
    overflow: visible;
    box-shadow: none;
  }

  .brief-section:first-child {
    padding-top: 0.25rem;
  }

  .brief-section:last-child {
    border-bottom: 0;
    padding-bottom: 0.25rem;
  }

  .doc-kicker,
  .section-title,
  .method-reco-label,
  .method-subhead,
  .findout-label {
    font-size: 0.75rem;
    font-weight: 500;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    margin: 0;
  }

  .doc-kicker {
    color: var(--primary);
    margin-bottom: 0.65rem;
  }

  .section-title {
    color: var(--darkprimary);
    margin: 0 0 0.85rem 0;
  }

  .doc-sub {
    margin: -0.45rem 0 0.9rem 0;
    color: var(--grey-secondary);
    font-size: 0.95rem;
  }

  .problem-question {
    margin: 0 0 0.75rem 0;
    font-size: 1.7rem;
    font-weight: 500;
    line-height: 1.28;
    color: var(--darkprimary);
  }

  .problem-question:last-child {
    margin-bottom: 0;
  }

  .section-content {
    color: var(--grey);
    line-height: 1.65;
    padding-left: 0;
  }

  .section-content p {
    margin: 0 0 1rem 0;
    position: relative;
  }

  .section-content p:last-child {
    margin-bottom: 0;
  }

  .timing-timeline {
    list-style: none;
    margin: 0;
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: 0;
  }

  .timing-row {
    display: grid;
    grid-template-columns: 1.25rem 1fr;
    gap: var(--space-3);
    align-items: stretch;
  }

  .timing-rail {
    position: relative;
    display: flex;
    flex-direction: column;
    align-items: center;
    padding-top: 0.35rem;
  }

  .timing-dot {
    width: 0.65rem;
    height: 0.65rem;
    border-radius: 50%;
    background: var(--accent-teal);
    border: 2px solid var(--white);
    box-shadow: 0 0 0 1px var(--accent-teal);
    flex-shrink: 0;
    z-index: 1;
  }

  .timing-stem {
    flex: 1;
    width: 2px;
    min-height: 1.25rem;
    margin-top: 0.25rem;
    background: var(--border-color);
  }

  .timing-body {
    padding: 0 0 var(--space-4) 0;
    min-width: 0;
  }

  .timing-row:last-child .timing-body {
    padding-bottom: 0;
  }

  .timing-phase {
    display: inline-block;
    font-size: var(--text-xs);
    font-weight: 600;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    color: var(--accent-teal-ink);
    background: var(--accent-teal-soft);
    padding: 0.2rem 0.55rem;
    border-radius: var(--radius-pill);
    margin-bottom: var(--space-2);
    font-variant-numeric: tabular-nums;
  }

  .timing-activity {
    margin: 0 !important;
    font-size: var(--text-base);
    color: var(--grey);
    line-height: 1.55;
  }

  /* Theme groups for "What We're Exploring" section */
  .theme-group {
    margin-bottom: 1.5rem;
  }

  .theme-group:last-child {
    margin-bottom: 0;
  }

  .theme-title {
    font-size: 1rem;
    font-weight: 500;
    color: var(--grey);
    margin: 0 0 0.5rem 0;
    padding-left: 0;
  }

  .theme-questions {
    margin: 0 0 0 1.5rem;
    padding-left: 0.5rem;
    list-style-type: decimal;
  }

  .theme-questions li {
    margin-bottom: 0.35rem;
    color: var(--grey-secondary);
    line-height: 1.6;
  }

  .theme-questions li:last-child {
    margin-bottom: 0;
  }

  .num-circle {
    display: grid;
    place-items: center;
    box-sizing: border-box;
    flex: 0 0 30px;
    width: 30px;
    height: 30px;
    min-width: 30px;
    max-width: 30px;
    min-height: 30px;
    max-height: 30px;
    margin: 1px 0 0;
    padding: 0;
    border-radius: 50%;
    background: var(--primary);
    color: var(--white);
    font-family: Arial, sans-serif;
    font-size: 22px;
    font-weight: 700;
    line-height: 1;
    text-align: center;
    align-self: flex-start;
    overflow: hidden;
    aspect-ratio: 1;
  }

  .numbered-list {
    list-style: none;
    margin: 0;
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: 0.85rem;
  }

  .numbered-list li {
    display: flex;
    gap: 0.7rem;
    align-items: flex-start;
    color: var(--grey);
    line-height: 1.55;
  }

  .findout-area {
    margin: 0;
    padding: 1.1rem 0;
    background: transparent;
    border: 0;
    border-bottom: 1px solid var(--border-color);
    border-radius: 0;
    text-align: left;
  }

  .findout-area:last-of-type {
    border-bottom: 0;
  }

  .findout-head {
    display: flex;
    align-items: center;
    gap: 0.55rem;
    margin-bottom: 0.45rem;
  }

  .findout-left-out,
  .method-steps li,
  .method-options article {
    margin: 0 0 0.85rem 0;
    padding: 0.95rem 1rem;
    background: var(--greyish);
    border: 1px solid var(--border-color);
    border-radius: 12px;
    text-align: left;
    color: var(--grey);
  }

  .findout-left-out {
    margin-top: 1rem;
    border-style: dashed;
  }

  .named-cards {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 0.75rem;
  }

  .named-cards.named-stack {
    grid-template-columns: 1fr;
  }

  .named-cards article {
    margin: 0;
    padding: 0.95rem 1rem;
    background: var(--white);
    border: 1px solid var(--border-color);
    border-left: 3px solid var(--primary);
    border-radius: 12px;
    text-align: left;
  }

  .named-cards h4 {
    margin: 0 0 0.4rem 0;
    color: var(--darkprimary);
    font-size: 1rem;
    font-weight: 500;
  }

  .findout-label {
    margin: 0;
    color: var(--primary);
  }

  .findout-left-out .findout-label {
    text-transform: none;
    letter-spacing: 0;
    font-size: 1rem;
    color: var(--darkprimary);
    margin-bottom: 0.45rem;
  }

  .findout-question {
    margin: 0 0 0.55rem 0;
    font-size: 1.28rem;
    font-weight: 500;
    line-height: 1.35;
    color: var(--darkprimary);
    text-align: left;
  }

  .findout-why,
  .method-have {
    margin: 0;
    color: var(--grey);
  }

  .findout-why span,
  .method-have span {
    font-weight: 500;
    color: var(--darkprimary);
  }

  .method-have {
    margin-top: 0.75rem;
    padding: 0.7rem 0.8rem;
    background: #e8f0fd;
    border-radius: 8px;
    color: var(--grey);
  }

  .findout-left-out ul,
  .method-steps {
    margin: 0.5rem 0 0 1.1rem;
    padding: 0;
  }

  .method-steps {
    list-style: none;
    margin-left: 0;
  }

  .method-badge {
    display: inline-block;
    margin: 0 0 0.9rem 0;
    padding: 0.28rem 0.8rem;
    border-radius: 999px;
    background: var(--primary);
    color: var(--white);
    font-size: 0.8rem;
    font-weight: 500;
  }

  .method-reco {
    margin: 0 0 1.1rem 0;
    padding: 0 0 0 0.9rem;
    border-left: 3px solid var(--primary);
  }

  .method-reco-label {
    color: var(--primary);
    margin-bottom: 0.4rem;
  }

  .method-lead {
    margin: 0;
    font-size: 1.15rem;
    line-height: 1.45;
    color: var(--darkprimary);
  }

  .method-step-head {
    display: flex;
    align-items: center;
    gap: 0.6rem;
    margin-bottom: 0.55rem;
  }

  .method-step-name {
    font-weight: 500;
    color: var(--grey);
    flex: 1;
  }

  .method-timing {
    font-size: 0.75rem;
    font-weight: 500;
    color: var(--darkprimary);
    background: #e8f0fd;
    border-radius: 999px;
    padding: 0.15rem 0.55rem;
    white-space: nowrap;
  }

  .method-subhead {
    margin: 1.35rem 0 0.55rem 0;
    color: var(--darkprimary);
    text-align: left;
  }

  .method-options {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
    gap: 0.75rem;
  }

  .empty-brief {
    padding: 4rem 2rem;
    text-align: center;
    color: var(--grey-secondary);
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 1rem;
  }

  .empty-brief i {
    font-size: 3rem;
    color: var(--grey-secondary);
    opacity: 0.5;
  }

  /* Incomplete Brief State */
  .incomplete-brief-state {
    margin-top: 2rem;
  }

  .incomplete-brief {
    padding: 3rem 2rem;
    display: flex;
    align-items: flex-start;
    gap: 2rem;
    background: var(--status-warn-soft);
    border: 2px dashed var(--status-warn);
    border-radius: var(--radius-card);
    margin: 0;
  }

  .incomplete-icon {
    font-size: 3rem;
    color: var(--status-warn-ink);
    flex-shrink: 0;
  }

  .incomplete-content {
    flex: 1;
  }

  .incomplete-content h3 {
    font-size: 1.25rem;
    font-weight: 500;
    color: var(--grey);
    margin: 0 0 0.75rem 0;
  }

  .incomplete-content p {
    font-size: 1rem;
    color: var(--grey-secondary);
    margin: 0 0 1.5rem 0;
    line-height: 1.6;
  }

  .incomplete-actions {
    display: flex;
    gap: 1rem;
  }

  /* Version History Count */
  .version-count {
    font-size: 0.85rem;
    color: var(--grey-secondary);
    font-weight: 400;
  }
  
  /* Your Answers Section - Full Width */
  .answers-list-full {
    padding: 2rem;
  }
  
  .answer-item-full {
    background: var(--greyish);
    border: 2px solid var(--border-color);
    border-radius: 12px;
    padding: 1.5rem;
    margin-bottom: 1.5rem;
    transition: all 0.2s ease;
  }
  
  .answer-item-full:hover {
    background: white;
    border-color: var(--secondary);
    box-shadow: 0 4px 12px rgba(5, 108, 242, 0.1);
    transform: translateY(-2px);
  }
  
  .answer-item-full:last-child {
    margin-bottom: 0;
  }
  
  .answer-question-full {
    display: flex;
    align-items: flex-start;
    gap: 1rem;
    margin-bottom: 1rem;
    padding-bottom: 1rem;
    border-bottom: 2px solid var(--border-color);
  }
  
  .question-number {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 40px;
    height: 40px;
    background: linear-gradient(135deg, var(--primary) 0%, var(--secondary) 100%);
    color: white;
    border-radius: 10px;
    font-size: 1rem;
    font-weight: 600;
    flex-shrink: 0;
    box-shadow: 0 2px 8px rgba(5, 108, 242, 0.3);
  }
  
  .question-text-full {
    flex: 1;
    font-size: 1.125rem;
    font-weight: 500;
    color: var(--grey);
    line-height: 1.5;
  }
  
  .answer-text-full {
    font-size: 1rem;
    color: var(--grey);
    line-height: 1.7;
    white-space: pre-wrap;
    padding-left: 3rem;
  }

  .answer-fields {
    display: flex;
    flex-direction: column;
    gap: var(--space-3);
    white-space: normal;
  }

  .answer-field-label {
    font-size: var(--text-xs);
    font-weight: 500;
    color: var(--grey-secondary);
    margin-bottom: 0.15rem;
  }

  .answer-field-value {
    font-size: var(--text-base);
    font-weight: 500;
    color: var(--grey);
  }

  /* Answer Feedback (integrated with answers) */
  .answer-feedback {
    display: flex;
    gap: 0.75rem;
    margin-top: 1rem;
    margin-left: 3rem;
    padding: 0.875rem 1rem;
    border-radius: 8px;
    background: white;
    border-left: 3px solid;
  }

  .answer-feedback.score-excellent {
    border-left-color: #45c496;
    background: rgba(69, 196, 150, 0.08);
  }

  .answer-feedback.score-good {
    border-left-color: #4790ee;
    background: rgba(71, 144, 238, 0.08);
  }

  .answer-feedback.score-average {
    border-left-color: #ffc107;
    background: rgba(255, 193, 7, 0.08);
  }

  .answer-feedback.score-poor {
    border-left-color: #dc3545;
    background: rgba(220, 53, 69, 0.08);
  }

  .answer-feedback i {
    font-size: 1rem;
    flex-shrink: 0;
    margin-top: 0.125rem;
  }

  .answer-feedback.score-excellent i {
    color: #45c496;
  }

  .answer-feedback.score-good i {
    color: #4790ee;
  }

  .answer-feedback.score-average i {
    color: #ffc107;
  }

  .answer-feedback.score-poor i {
    color: #dc3545;
  }

  .answer-feedback .feedback-content {
    flex: 1;
  }

  .answer-feedback p {
    margin: 0;
    font-size: 0.875rem;
    color: var(--grey);
    line-height: 1.5;
  }

  .answer-feedback .feedback-fallback {
    font-style: italic;
    color: var(--grey-secondary);
  }

  /* No Answers State */
  .no-answers {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 4rem 2rem;
    text-align: center;
  }

  .no-answers-icon {
    width: 80px;
    height: 80px;
    border-radius: 50%;
    background: var(--greyish);
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 1.5rem;
  }

  .no-answers-icon i {
    font-size: 2.5rem;
    color: var(--grey-secondary);
  }

  .no-answers h3 {
    font-size: 1.25rem;
    font-weight: 500;
    color: var(--grey);
    margin: 0 0 0.75rem 0;
  }

  .no-answers p {
    font-size: 1rem;
    color: var(--grey-secondary);
    margin: 0 0 1.5rem 0;
    max-width: 400px;
    line-height: 1.6;
  }
  
  .view-latest-btn {
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.75rem 1.5rem;
    background: linear-gradient(135deg, var(--primary) 0%, var(--secondary) 100%);
    color: white;
    border: none;
    border-radius: 8px;
    font-size: 0.9375rem;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s ease;
    box-shadow: 0 2px 8px rgba(5, 108, 242, 0.3);
  }
  
  .view-latest-btn:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(5, 108, 242, 0.4);
  }
  
  .view-latest-btn i {
    font-size: 1rem;
  }

  .version-list {
    padding: 1rem;
    max-height: 65vh;
    overflow-y: auto;
  }

  .version-item {
    background: var(--white);
    border: 1px solid var(--border-color);
    border-radius: 8px;
    margin-bottom: 0.75rem;
    transition: all 0.2s ease;
    display: flex;
    align-items: center;
    gap: 0.75rem;
    overflow: hidden;
  }

  .version-item:hover {
    border-color: var(--secondary);
    box-shadow: 0 2px 8px rgba(5, 108, 242, 0.1);
  }

  .version-item.viewing {
    border: 2px solid var(--primary);
    background: linear-gradient(135deg, rgba(5, 108, 242, 0.05) 0%, rgba(255, 255, 255, 1) 100%);
    box-shadow: 0 4px 12px rgba(5, 108, 242, 0.2);
  }

  .version-item:last-child {
    margin-bottom: 0;
  }
  
  .version-main {
    flex: 1;
    padding: 1rem;
    cursor: pointer;
    transition: background 0.2s ease;
  }
  
  .version-main:hover {
    background: var(--greyish);
  }
  
  .version-modify-btn {
    padding: 0.75rem 1.25rem;
    margin-right: 0.75rem;
    background: #45c496;
    color: white;
    border: none;
    border-radius: 6px;
    font-size: 0.875rem;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s ease;
    display: flex;
    align-items: center;
    gap: 0.5rem;
    flex-shrink: 0;
    box-shadow: 0 2px 4px rgba(69, 196, 150, 0.2);
  }
  
  .version-modify-btn:hover {
    background: #3ab085;
    transform: translateY(-1px);
    box-shadow: 0 4px 8px rgba(69, 196, 150, 0.3);
  }
  
  .version-modify-btn:active {
    transform: translateY(0);
  }
  
  .version-modify-btn i {
    font-size: 1rem;
  }

  .version-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 0.75rem;
  }

  .version-info {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 0.25rem;
  }

  .version-title-display {
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }

  .version-title {
    font-size: 0.875rem;
    color: var(--grey);
    font-weight: 400;
  }

  .version-title-placeholder {
    font-size: 0.875rem;
    color: var(--grey-secondary);
    font-style: italic;
  }

  .title-edit-btn {
    background: transparent;
    border: none;
    color: var(--grey-secondary);
    cursor: pointer;
    padding: 0.25rem;
    opacity: 0;
    transition: all 0.2s ease;
  }

  .version-item:hover .title-edit-btn {
    opacity: 1;
  }

  .title-edit-btn:hover {
    color: var(--primary);
  }

  .title-edit-btn i {
    font-size: 0.75rem;
  }

  .title-edit-wrapper {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    width: 100%;
  }

  .title-edit-input {
    flex: 1;
    padding: 0.5rem;
    border: 2px solid var(--primary);
    border-radius: 4px;
    font-size: 0.875rem;
    color: var(--grey);
    background: white;
    font-family: "Poppins", sans-serif;
  }

  .title-edit-input:focus {
    outline: none;
    box-shadow: 0 0 0 3px rgba(5, 108, 242, 0.1);
  }

  .title-edit-actions {
    display: flex;
    gap: 0.25rem;
  }

  .title-save-btn,
  .title-cancel-btn {
    padding: 0.5rem;
    border: none;
    border-radius: 4px;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.2s ease;
  }

  .title-save-btn {
    background: #45c496;
    color: white;
  }

  .title-save-btn:hover {
    background: #3ab386;
  }

  .title-cancel-btn {
    background: var(--greyish);
    color: var(--grey);
  }

  .title-cancel-btn:hover {
    background: var(--grey-secondary);
    color: white;
  }

  .title-save-btn i,
  .title-cancel-btn i {
    font-size: 0.875rem;
    margin: 0;
  }

  .version-number {
    font-size: 1rem;
    font-weight: 500;
    color: var(--grey);
  }

  .version-date {
    display: flex;
    align-items: center;
    gap: 0.25rem;
    font-size: 0.75rem;
    color: var(--grey-secondary);
  }

  .version-date i {
    font-size: 0.7rem;
  }

  .version-alignment {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  .alignment-label {
    font-size: 0.8rem;
    color: var(--grey-secondary);
    font-weight: 400;
  }

  .alignment-details {
    display: flex;
    align-items: center;
    gap: 0.75rem;
  }

  .alignment-percent {
    font-size: 1.1rem;
    font-weight: 500;
    color: var(--grey);
  }

  .alignment-status {
    display: inline-flex;
    align-items: center;
    gap: 0.25rem;
    padding: 0.25rem 0.75rem;
    border-radius: 9999px;
    font-size: 0.75rem;
    font-weight: 500;
  }

  .alignment-excellent {
    background: #d4f4dd;
    color: #0f5132;
  }

  .alignment-good {
    background: #cfe2ff;
    color: #084298;
  }

  .alignment-average {
    background: #fff3cd;
    color: #664d03;
  }

  .alignment-poor {
    background: #f8d7da;
    color: #842029;
  }

  .alignment-default {
    background: var(--greyish);
    color: var(--grey-secondary);
  }

  /* Score Indicator Bar Styles */
  .score-indicator-bar {
    display: flex;
    align-items: center;
    padding: 0.75rem 1.25rem;
    margin: 0 2rem 1.5rem 2rem;
    border-radius: 8px;
    border-left: 4px solid;
  }

  .score-indicator-bar.alignment-excellent {
    background: rgba(69, 196, 150, 0.1);
    border-left-color: #45c496;
  }

  .score-indicator-bar.alignment-good {
    background: rgba(71, 144, 238, 0.1);
    border-left-color: #4790ee;
  }

  .score-indicator-bar.alignment-average {
    background: rgba(255, 193, 7, 0.1);
    border-left-color: #ffc107;
  }

  .score-indicator-bar.alignment-poor {
    background: rgba(217, 27, 66, 0.1);
    border-left-color: #d91b42;
  }

  .score-indicator-content {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    flex-wrap: wrap;
  }

  .score-indicator-label {
    font-size: 0.875rem;
    color: var(--grey-secondary);
  }

  .score-indicator-value {
    font-size: 1.125rem;
    font-weight: 600;
  }

  .score-indicator-bar.alignment-excellent .score-indicator-value {
    color: #0f5132;
  }

  .score-indicator-bar.alignment-good .score-indicator-value {
    color: #084298;
  }

  .score-indicator-bar.alignment-average .score-indicator-value {
    color: #664d03;
  }

  .score-indicator-bar.alignment-poor .score-indicator-value {
    color: #842029;
  }

  .score-indicator-status {
    display: inline-flex;
    align-items: center;
    gap: 0.25rem;
    font-size: 0.875rem;
    font-weight: 500;
    padding: 0.25rem 0.625rem;
    border-radius: 9999px;
  }

  .score-indicator-bar.alignment-excellent .score-indicator-status {
    background: #d4f4dd;
    color: #0f5132;
  }

  .score-indicator-bar.alignment-good .score-indicator-status {
    background: #cfe2ff;
    color: #084298;
  }

  .score-indicator-bar.alignment-average .score-indicator-status {
    background: #fff3cd;
    color: #664d03;
  }

  .score-indicator-bar.alignment-poor .score-indicator-status {
    background: #f8d7da;
    color: #842029;
  }

  .score-indicator-status i {
    font-size: 0.875rem;
  }

  .score-status {
    display: inline-flex;
    align-items: center;
    gap: 0.375rem;
    font-size: 0.875rem;
    font-weight: 500;
  }

  .score-badge.alignment-excellent .score-value {
    color: #0f5132;
  }

  .score-badge.alignment-good .score-value {
    color: #084298;
  }

  .score-badge.alignment-average .score-value {
    color: #664d03;
  }

  .score-badge.alignment-poor .score-value {
    color: #842029;
  }

  .question-score {
    display: inline-flex;
    align-items: center;
    padding: 0.25rem 0.75rem;
    border-radius: 9999px;
    font-size: 0.75rem;
    font-weight: 500;
    margin-left: auto;
  }

  .question-score.score-excellent {
    background: #d4f4dd;
    color: #0f5132;
  }

  .question-score.score-good {
    background: #cfe2ff;
    color: #084298;
  }

  .question-score.score-average {
    background: #fff3cd;
    color: #664d03;
  }

  .question-score.score-poor {
    background: #f8d7da;
    color: #842029;
  }

  .answer-question-full {
    display: flex;
    align-items: center;
    gap: 0.75rem;
  }

  /* Responsive Design */
  @media (max-width: 768px) {
    .content-grid {
      grid-template-columns: 1fr;
    }

    .brief-header {
      flex-direction: column;
      align-items: flex-start;
    }

    .top-actions {
      width: 100%;
    }

    .action-btn {
      width: 100%;
      justify-content: center;
    }

    .brief-sections {
      padding: 1.25rem;
      gap: 1.25rem;
    }

    .brief-section {
      padding: 1.35rem 0;
    }

    .problem-question {
      font-size: 1.3rem;
    }

    .named-cards {
      grid-template-columns: 1fr;
    }

    .section-content {
      padding-left: 0;
    }
    
    .tabs-nav {
      border-radius: 8px 8px 0 0;
    }
    
    .tab-btn {
      padding: 1rem 0.75rem;
      font-size: 0.9rem;
      flex-direction: column;
      gap: 0.25rem;
    }
    
    .tab-btn i {
      font-size: 1.25rem;
    }
    
    .answer-item-full {
      padding: 1.25rem;
      margin-bottom: 1.25rem;
    }
    
    .answer-question-full {
      gap: 0.75rem;
    }
    
    .question-number {
      min-width: 36px;
      height: 36px;
      font-size: 0.9rem;
    }
    
    .question-text-full {
      font-size: 1rem;
    }
    
    .answer-text-full {
      font-size: 0.9375rem;
      padding-left: 2.5rem;
    }

    .answer-feedback {
      margin-left: 2.5rem;
      padding: 0.75rem;
    }

    .brief-title {
      font-size: 1.5rem;
    }

    .draft-alert {
      flex-direction: column;
    }

    .draft-actions {
      width: 100%;
      flex-direction: row;
    }

    .btn-resume,
    .btn-discard {
      flex: 1;
    }

    .version-list {
      max-height: none;
    }

    .incomplete-brief {
      flex-direction: column;
      padding: 2rem 1.5rem;
      text-align: center;
    }

    .incomplete-icon {
      font-size: 2.5rem;
    }

    .incomplete-actions {
      flex-direction: column;
      width: 100%;
    }

    .incomplete-actions .action-btn {
      width: 100%;
      justify-content: center;
    }

    .brief-title {
      font-size: 1.5rem;
    }

    .title-skeleton {
      font-size: 1rem;
    }
  }

  /* Modal Base Styles */
  .modal-overlay {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: var(--shade-background-color);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 2000;
    animation: fadeIn 0.2s ease-out;
  }

  @keyframes fadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }

  .modal-content {
    background-color: var(--white);
    border-radius: 12px;
    box-shadow: 0 10px 40px rgba(0, 0, 0, 0.2);
    max-height: 90vh;
    overflow-y: auto;
    animation: slideUp 0.3s ease-out;
  }

  @keyframes slideUp {
    from {
      transform: translateY(20px);
      opacity: 0;
    }
    to {
      transform: translateY(0);
      opacity: 1;
    }
  }

  .modal-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 1.25rem 1.5rem;
    border-bottom: 1px solid var(--border-color);
  }

  .modal-header h3 {
    margin: 0;
    font-size: 1.25rem;
    font-weight: 500;
    color: var(--grey);
  }

  .modal-close {
    background: none;
    border: none;
    cursor: pointer;
    padding: 0.5rem;
    color: var(--grey-secondary);
    transition: color 0.2s ease;
  }

  .modal-close:hover {
    color: var(--grey);
  }

  .modal-close i {
    font-size: 1.25rem;
  }

  .modal-body {
    padding: 1.5rem;
  }

  /* Proposal CTA Section */
  .proposal-cta-section {
    margin: 2rem;
    margin-bottom: 3rem;
    background: linear-gradient(135deg, rgba(5, 108, 242, 0.05) 0%, rgba(71, 144, 238, 0.08) 100%);
    border: 2px solid rgba(5, 108, 242, 0.15);
    border-radius: 16px;
    padding: 2rem;
    position: relative;
    overflow: hidden;
  }

  .proposal-cta-section::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 4px;
    height: 100%;
    background: linear-gradient(180deg, var(--primary) 0%, var(--secondary) 100%);
  }

  .proposal-cta-content {
    display: flex;
    align-items: center;
    gap: 1.5rem;
    flex-wrap: wrap;
  }

  .proposal-cta-icon {
    flex-shrink: 0;
    width: 60px;
    height: 60px;
    border-radius: 50%;
    background: linear-gradient(135deg, var(--primary) 0%, var(--secondary) 100%);
    display: flex;
    align-items: center;
    justify-content: center;
    box-shadow: 0 4px 12px rgba(5, 108, 242, 0.25);
  }

  .proposal-cta-icon i {
    font-size: 1.5rem;
    color: white;
  }

  .proposal-cta-text {
    flex: 0 1 auto;
    max-width: min(36rem, 100%);
  }

  .proposal-cta-text h3 {
    margin: 0 0 0.5rem 0;
    font-size: 1.25rem;
    font-weight: 500;
    color: var(--grey);
  }

  .proposal-cta-text p {
    margin: 0;
    color: var(--grey-secondary);
    line-height: 1.6;
  }

  .proposal-cta-btn {
    flex-shrink: 0;
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.875rem 1.5rem;
    background: linear-gradient(135deg, var(--primary) 0%, var(--secondary) 100%);
    color: white;
    border: none;
    border-radius: 8px;
    font-size: 1rem;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s ease;
    box-shadow: 0 2px 8px rgba(5, 108, 242, 0.25);
  }

  .proposal-cta-btn:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(5, 108, 242, 0.35);
  }

  .proposal-cta-btn i {
    font-size: 1rem;
  }

  /* Proposal Modal */
  .proposal-modal {
    max-width: 500px;
    width: 90%;
  }

  .modal-description {
    color: var(--grey-secondary);
    margin-bottom: 1.5rem;
    line-height: 1.6;
  }

  .form-group {
    margin-bottom: 1.25rem;
  }

  .form-group label {
    display: block;
    margin-bottom: 0.5rem;
    font-weight: 400;
    color: var(--grey);
  }

  .form-group .required {
    color: #d91b42;
  }

  .form-group .optional {
    color: var(--grey-secondary);
    font-weight: 300;
    font-size: 0.875rem;
  }

  .form-group input,
  .form-group textarea {
    width: 100%;
    padding: 0.75rem 1rem;
    border: 1px solid var(--border-color);
    border-radius: var(--border-radius-button);
    font-size: 1rem;
    transition: border-color 0.2s ease, box-shadow 0.2s ease;
    background: white;
  }

  .form-group input:focus,
  .form-group textarea:focus {
    outline: none;
    border-color: var(--primary);
    box-shadow: 0 0 0 3px rgba(5, 108, 242, 0.1);
  }

  .form-group textarea {
    resize: vertical;
    min-height: 80px;
  }

  .modal-actions {
    display: flex;
    gap: 1rem;
    margin-top: 1.5rem;
    justify-content: flex-end;
  }

  .btn-cancel {
    padding: 0.75rem 1.25rem;
    background: var(--greyish);
    color: var(--grey);
    border: 1px solid var(--border-color);
    border-radius: 6px;
    font-size: 0.9375rem;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s ease;
  }

  .btn-cancel:hover {
    background: #e9ecef;
  }

  .btn-submit {
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.75rem 1.25rem;
    background: linear-gradient(135deg, var(--primary) 0%, var(--secondary) 100%);
    color: white;
    border: none;
    border-radius: 6px;
    font-size: 0.9375rem;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s ease;
  }

  .btn-submit:hover:not(:disabled) {
    transform: translateY(-1px);
    box-shadow: 0 4px 8px rgba(5, 108, 242, 0.25);
  }

  .btn-submit:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  /* Proposal Success State */
  .proposal-success {
    text-align: center;
    padding: 2rem 1rem;
  }

  .success-icon {
    width: 80px;
    height: 80px;
    margin: 0 auto 1.5rem;
    border-radius: 50%;
    background: linear-gradient(135deg, #45c496 0%, #3ab085 100%);
    display: flex;
    align-items: center;
    justify-content: center;
    animation: scale-in 0.3s ease-out;
  }

  .success-icon i {
    font-size: 2.5rem;
    color: white;
  }

  @keyframes scale-in {
    0% {
      transform: scale(0);
      opacity: 0;
    }
    50% {
      transform: scale(1.1);
    }
    100% {
      transform: scale(1);
      opacity: 1;
    }
  }

  .proposal-success h3 {
    margin: 0 0 1rem 0;
    font-size: 1.5rem;
    font-weight: 500;
    color: var(--grey);
  }

  .proposal-success p {
    color: var(--grey-secondary);
    line-height: 1.6;
    margin: 0;
  }

  .btn-ok {
    margin-top: 1.5rem;
    padding: 0.75rem 2rem;
    background: var(--primary);
    color: white;
    border: none;
    border-radius: 6px;
    font-size: 1rem;
    font-weight: 500;
    cursor: pointer;
    transition: background-color 0.2s;
  }

  .btn-ok:hover {
    background: var(--darkprimary);
  }

  /* Responsive Proposal CTA */
  @media (max-width: 900px) {
    .proposal-cta-content {
      flex-direction: column;
      align-items: flex-start;
      text-align: left;
    }

    .proposal-cta-btn {
      width: auto;
      justify-content: center;
    }
  }

  @media (max-width: 768px) {
    .proposal-cta-content {
      align-items: center;
      text-align: center;
    }

    .proposal-cta-text {
      text-align: center;
    }

    .proposal-cta-btn {
      width: 100%;
      justify-content: center;
    }

    .modal-actions {
      flex-direction: column-reverse;
    }

    .btn-cancel,
    .btn-submit {
      width: 100%;
      justify-content: center;
    }
  }
</style>

